import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
    Activity, Layers, ChevronRight, ChevronLeft, ChevronDown,
    ArrowLeft, ArrowRightLeft, Clock, IndianRupee,
    CalendarDays, Search, X, Menu, UserCircle, Settings, Check, Filter, Download,
    HelpCircle, PlayCircle, Edit, User
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Modal from '../../ui/Modal'; 
import EditWorkerModal from '../../ui/EditWorkerModal';

const API_BASE = 'https://mukadampro.shirpur.store';

// --- HELPER: GET DATE STRING ---
const getDateString = (date) => { 
    try { 
        if (!date) return new Date().toISOString().split('T')[0]; 
        const d = new Date(date); 
        if (isNaN(d.getTime())) return new Date().toISOString().split('T')[0]; 
        const offset = d.getTimezoneOffset() * 60000; 
        return new Date(d.getTime() - offset).toISOString().split('T')[0]; 
    } catch(e) { 
        return new Date().toISOString().split('T')[0]; 
    } 
};

// --- HELPER: FORMAT TIME ---
const formatTimeForDisplay = (t) => { 
    if (!t) return ""; 
    let timeStr = String(t).trim().replace(/^\d{4}-\d{2}-\d{2}\s+/, ''); 
    if (timeStr.toUpperCase().includes('PM') || timeStr.toUpperCase().includes('AM')) return timeStr; 
    let [h, m] = timeStr.split(':'); 
    h = parseInt(h, 10) || 0; 
    const ampm = h >= 12 ? 'PM' : 'AM'; 
    return `${h % 12 || 12}:${m || '00'} ${ampm}`; 
};

// --- HELPER: PARSE TIME STR ---
const parseTimeStr = (t) => { 
    if (!t) return 0;
    let timeStr = String(t).trim();
    timeStr = timeStr.replace(/^\d{4}-\d{2}-\d{2}\s+/, '');
    
    let isPM = timeStr.toUpperCase().includes('PM');
    let isAM = timeStr.toUpperCase().includes('AM');
    
    let cleanTime = timeStr.replace(/PM|AM/i, '').trim();
    let [h, m] = cleanTime.split(':').map(Number);
    
    h = h || 0;
    m = m || 0;
    
    return h * 60 + m; 
};

// --- HELPER: EXTRACT LOOMS FOR HEADER ---
const getWorkerLoomsHeaderStr = (userData) => {
    let rawLoomsData = null;
    if (userData) {
        rawLoomsData = userData.looms || userData.looms_assigned || userData.assigned_looms || userData.loom_ids || userData.loom || userData.looms_list;
    }
    if (!rawLoomsData) {
        const storageKeys = ['user', 'worker', 'workerData', 'loggedInUser', 'worker_user', 'auth_user'];
        for (const key of storageKeys) {
            try {
                const item = localStorage.getItem(key);
                if (item) {
                    const parsed = JSON.parse(item);
                    if (parsed) {
                        rawLoomsData = parsed.looms || parsed.looms_assigned || parsed.assigned_looms || parsed.loom_ids || parsed.loom || parsed.looms_list;
                        if (rawLoomsData) break;
                    }
                }
            } catch (e) {}
        }
    }
    if (!rawLoomsData) rawLoomsData = localStorage.getItem('worker_looms') || localStorage.getItem('looms');
    if (!rawLoomsData) return '';

    let loomIds = [];
    if (Array.isArray(rawLoomsData)) {
        loomIds = rawLoomsData.map(item => (typeof item === 'object' && item !== null) ? String(item.loom_id || item.id || '') : String(item));
    } else if (typeof rawLoomsData === 'string' || typeof rawLoomsData === 'number') {
        loomIds = String(rawLoomsData).split(',').map(s => s.trim());
    }
    const cleanLoomIds = loomIds.filter(id => id && id.trim() !== '' && id !== 'null' && id !== 'undefined');
    if (cleanLoomIds.length === 0) return '';

    const formattedLooms = cleanLoomIds.map(id => `L${String(id).trim().replace(/^L/i, '')}`).join(', ');
    return `${cleanLoomIds.length} LOOMS: ${formattedLooms}`;
};



export default function OwnersWorkerHistory({ 
    worker: initialWorkerProp, 
    onBack, 
    onEdit, 
    availableLooms, 
    refreshData, 
    ownerProfile, 
    globalGuide, 
    appBg 
}) {
    const [worker, setWorker] = useState(initialWorkerProp);

    useEffect(() => {
        if (initialWorkerProp) {
            setWorker(initialWorkerProp);
        }
    }, [initialWorkerProp]);

    // Smart Guide destructuring from owner props
    const {
        voiceAssist = false, 
        showGuideModal = false, 
        setShowGuideModal = () => {},
        activeGuideFlow = null, 
        currentGuideMessage = "",
        handleVoiceClick = null,
        handleFocusVoice = null,
        startSmartGuide = () => {}, 
        endSmartGuide = () => {}, 
        advanceNextStep = () => {},
        speakHinglish = () => {}
    } = globalGuide || {};

    const appBgFinal = appBg || localStorage.getItem('appBg') || "linear-gradient(135deg, #f1f5f9, #e2e8f0)";
    
    // --- REPORT MODAL & SEARCH STATES ---
    const [showReportModal, setShowReportModal] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [showEarnings, setShowEarnings] = useState(false);
    const [reportDateStart, setReportDateStart] = useState(getDateString(new Date()));
    const [reportDateEnd, setReportDateEnd] = useState(getDateString(new Date()));
    const [reportFrequency, setReportFrequency] = useState('daily');
    const [reportType, setReportType] = useState('detailed');
    const [isDownloading, setIsDownloading] = useState(false);
    const [monthViewMode, setMonthViewMode] = useState('weekly'); 

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period] = useState('total');
    
    const [selectedYearForMonths, setSelectedYearForMonths] = useState(null); 
    const [selectedMonthForDays, setSelectedMonthForDays] = useState(null);
    const [selectedWeekForDays, setSelectedWeekForDays] = useState(null); 
    const [selectedDayForLooms, setSelectedDayForLooms] = useState(null); 
    
    const ALL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const [currentMonthTab] = useState(ALL_MONTHS[new Date().getMonth()]); 

    const currentRowRef = useRef(null);

    // Derived properties matching worker
    const myLiveApiData = worker || {};
    const workerShift = worker?.shift;
    const workerLooms = useMemo(() => {
        if (!worker || !worker.looms) return [];
        return worker.looms.toString().split(',').map(id => ({ loom_id: id.trim() }));
    }, [worker]);

    useEffect(() => {
        if (!worker || !worker.id) return;
        setLoading(true);
        fetch(`${API_BASE}/get_worker_stats.php?id=${worker.id}&doj=${worker.doj || ''}&t=${Date.now()}`)
            .then(res => res.json())
            .then(json => {
                setData(json || { reports: { daily_wise: {}, month_wise: {}, year_wise: {} }, weekly: {} });
            })
            .catch(err => console.error("History fetch error:", err))
            .finally(() => setLoading(false));
    }, [worker]);

    useEffect(() => {
        if (currentRowRef.current) currentRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [period, selectedYearForMonths, selectedMonthForDays, currentMonthTab]);

    const workerFilteredData = useMemo(() => {
        if (!workerLooms || workerLooms.length === 0) {
            return { reports: { daily_wise: {}, month_wise: {}, year_wise: {} }, weekly: {} };
        }
        let newReports = { daily_wise: {}, month_wise: {}, year_wise: {} };
        let newWeekly = {};
        
        const extractWorkerData = (dayObj) => {
            let total = parseFloat(dayObj?.Total || 0);
            let dayM = parseFloat(dayObj?.Day || 0);
            let nightM = parseFloat(dayObj?.Night || 0);
            let otBadli = parseFloat(dayObj?.ot_badli || 0);
            let otType = dayObj?.ot_badli_type || ""; 
            let otStartTime = dayObj?.ot_start_time || dayObj?.ot_start || dayObj?.start_time || ""; 
            let otEndTime = dayObj?.ot_end_time || dayObj?.ot_end || dayObj?.end_time || "";     
            let looms = dayObj?.looms || {};
            let looms_ot = dayObj?.looms_ot || {};

            const rate = parseFloat(myLiveApiData?.rate_per_meter || myLiveApiData?.ratePerMeter || 0);
            let dayE = parseFloat(dayObj?.Day_earn) || (dayM * rate);
            let nightE = parseFloat(dayObj?.Night_earn) || (nightM * rate);
            let otBadliE = parseFloat(dayObj?.ot_badli_earn) || (otBadli * rate);
            let totalE = parseFloat(dayObj?.Total_earn) || (total * rate);

            return { total, day: dayM, night: nightM, otBadli, otType, otStartTime, otEndTime, looms, looms_ot, dayE, nightE, otBadliE, totalE };
        };

        if (data?.reports?.daily_wise) {
            Object.entries(data.reports.daily_wise).forEach(([year, months]) => {
                newReports.daily_wise[year] = {}; 
                newReports.month_wise[year] = {};
                
                let yearTotal = 0; let yearDay = 0; let yearNight = 0; let yearOtBadli = 0;
                let yearDayE = 0; let yearNightE = 0; let yearOtBadliE = 0; let yearTotalE = 0; 
                let yearOtTypes = new Set(); 
                
                Object.entries(months).forEach(([month, days]) => {
                    newReports.daily_wise[year][month] = {};
                    
                    let monthTotal = 0; let monthDay = 0; let monthNight = 0; let monthOtBadli = 0;
                    let monthDayE = 0; let monthNightE = 0; let monthOtBadliE = 0; let monthTotalE = 0; 
                    let monthOtTypes = new Set(); 
                    
                    Object.entries(days).forEach(([date, dayData]) => {
                        const { total, day, night, otBadli, otType, otStartTime, otEndTime, looms, looms_ot, dayE, nightE, otBadliE, totalE } = extractWorkerData(dayData);
                        if (otType) { otType.split(' & ').forEach(t => { monthOtTypes.add(t); yearOtTypes.add(t); }); }
                        
                        if (total > 0 || otBadli > 0) {
                            newReports.daily_wise[year][month][date] = { Total: total, Day: day, Night: night, looms: looms, looms_ot: looms_ot, otBadli: otBadli, otType: otType, otStartTime: otStartTime, otEndTime: otEndTime, dayE, nightE, otBadliE, totalE };
                            monthTotal += total; monthDay += day; monthNight += night; monthOtBadli += otBadli;
                            monthDayE += dayE; monthNightE += nightE; monthOtBadliE += otBadliE; monthTotalE += totalE; 
                        }
                    });
                    
                    if (monthTotal > 0 || monthOtBadli > 0) {
                        newReports.month_wise[year][month] = { Total: monthTotal, Day: monthDay, Night: monthNight, otBadli: monthOtBadli, otType: Array.from(monthOtTypes).join(' & '), dayE: monthDayE, nightE: monthNightE, otBadliE: monthOtBadliE, totalE: monthTotalE };
                        yearTotal += monthTotal; yearDay += monthDay; yearNight += monthNight; yearOtBadli += monthOtBadli;
                        yearDayE += monthDayE; yearNightE += monthNightE; yearOtBadliE += monthOtBadliE; yearTotalE += monthTotalE; 
                    }
                });
                
                if (yearTotal > 0 || yearOtBadli > 0) {
                    newReports.year_wise[year] = { Total: yearTotal, Day: yearDay, Night: yearNight, otBadli: yearOtBadli, otType: Array.from(yearOtTypes).join(' & '), dayE: yearDayE, nightE: yearNightE, otBadliE: yearOtBadliE, totalE: yearTotalE };
                }
            });
        }
        
        if (data?.weekly) {
            Object.entries(data.weekly).forEach(([date, dayData]) => {
                const { total, day, night, otBadli, otType, otStartTime, otEndTime, looms, looms_ot, dayE, nightE, otBadliE, totalE } = extractWorkerData(dayData);
                if (total > 0 || otBadli > 0) {
                    newWeekly[date] = { Total: total, Day: day, Night: night, looms, looms_ot, otBadli, otType, otStartTime, otEndTime, dayEarn: dayE, nightEarn: nightE, otBadliEarn: otBadliE, totalEarn: totalE };
                }
            });
        }
        return { reports: newReports, weekly: newWeekly };
    }, [data, workerLooms, myLiveApiData]);

    const reportData = useMemo(() => {
        const resultRows = [];
        const todayObj = new Date();
        const currentYearStr = todayObj.getFullYear().toString();
        const currentMonthStr = ALL_MONTHS[todayObj.getMonth()];
        const dailyWise = workerFilteredData.reports?.daily_wise || {};

        if (period === 'total') {
            const reports = workerFilteredData.reports || { month_wise: {}, year_wise: {}, daily_wise: {} };
            
            if (selectedYearForMonths && selectedMonthForDays && selectedWeekForDays) {
                const wStart = new Date(selectedWeekForDays);
                for(let i=0; i<7; i++) {
                    const d = new Date(wStart);
                    d.setDate(wStart.getDate() + i);
                    if (d > todayObj) break;

                    const localOffset = d.getTimezoneOffset() * 60000;
                    const dateStr = new Date(d.getTime() - localOffset).toISOString().split('T')[0];
                    const yearStr = d.getFullYear().toString();
                    const monthStr = ALL_MONTHS[d.getMonth()];
                    
                    const r = dailyWise[yearStr]?.[monthStr]?.[dateStr] || { Day: 0, Night: 0, Total: 0, otBadli: 0, otType: "", otStartTime: "", otEndTime: "", looms: {}, looms_ot: {} };
                    resultRows.push({
                        rawDate: dateStr, label: `${d.getDate()} ${d.toLocaleDateString('en-US', {month:'long'})}`, subLabel: d.toLocaleDateString('en-US', {weekday:'long'}),
                        dayEarn: r.dayE, nightEarn: r.nightE, otBadliEarn: r.otBadliE, totalEarn: r.totalE,
                        day: r.Day || 0, night: r.Night || 0, total: r.Total || 0, otBadli: r.otBadli || 0, otType: r.otType || "", otStartTime: r.otStartTime || "", otEndTime: r.otEndTime || "", looms: r.looms || {}, looms_ot: r.looms_ot || {}, isCurrent: dateStr === todayObj.toLocaleDateString('fr-CA')
                    });
                }
            } else if (selectedYearForMonths && selectedMonthForDays) {
                const monthData = dailyWise[selectedYearForMonths]?.[selectedMonthForDays] || {};
                const monthIndex = ALL_MONTHS.indexOf(selectedMonthForDays);
                const isCurrentMonth = selectedYearForMonths === currentYearStr && selectedMonthForDays === currentMonthStr;
                const daysInMonth = new Date(selectedYearForMonths, monthIndex + 1, 0).getDate();

                if (monthViewMode === 'daily') {
                    const todayStr = getDateString(todayObj);
                    for (let i = 1; i <= daysInMonth; i++) {
                        const d = new Date(selectedYearForMonths, monthIndex, i);
                        if (isCurrentMonth && d > todayObj) break;

                        const localOffset = d.getTimezoneOffset() * 60000;
                        const dateStr = new Date(d.getTime() - localOffset).toISOString().split('T')[0];
                        const r = monthData[dateStr] || { Day: 0, Night: 0, Total: 0, otBadli: 0, otType: "", otStartTime: "", otEndTime: "", looms: {}, looms_ot: {} };

                        resultRows.push({
                            rawDate: dateStr,
                            label: `${d.getDate()} ${ALL_MONTHS[d.getMonth()]}`,
                            subLabel: d.toLocaleDateString('en-US', {weekday:'long'}),
                            dayEarn: r.dayE || 0, nightEarn: r.nightE || 0, otBadliEarn: r.otBadliE || 0, totalEarn: r.totalE || 0,
                            day: r.Day || 0, night: r.Night || 0, total: r.Total || 0, otBadli: r.otBadli || 0, otType: r.otType || "", otStartTime: r.otStartTime || "", otEndTime: r.otEndTime || "", looms: r.looms || {}, looms_ot: r.looms_ot || {},
                            isCurrent: dateStr === todayStr
                        });
                    }
                } else {
                    const weeksMap = new Map();

                    for (let i = 1; i <= daysInMonth; i++) {
                        const d = new Date(selectedYearForMonths, monthIndex, i);
                        if (isCurrentMonth && d > todayObj) break;

                        const diffToSat = (d.getDay() + 1) % 7; 
                        const weekStart = new Date(d); weekStart.setDate(d.getDate() - diffToSat); weekStart.setHours(0,0,0,0);
                        const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6); weekEnd.setHours(23,59,59,999);

                        const jan1 = new Date(selectedYearForMonths, 0, 1);
                        const jan1Diff = (jan1.getDay() + 1) % 7;
                        const firstSat = new Date(jan1); firstSat.setDate(jan1.getDate() - jan1Diff); firstSat.setHours(0,0,0,0);
                        
                        const weekNum = Math.floor((weekStart - firstSat) / (7 * 24 * 60 * 60 * 1000)) + 1;
                        const weekKey = weekStart.getTime();

                        if (!weeksMap.has(weekKey)) {
                            const formatD = (dateObj) => `${dateObj.getDate()} ${ALL_MONTHS[dateObj.getMonth()].substring(0,3)}`;
                            const isTransition = (weekStart.getMonth() === 11 && weekEnd.getMonth() === 0);
                            const finalLabel = isTransition ? "TRANSITION WEEK" : `WEEK ${weekNum}`;

                            weeksMap.set(weekKey, {
                                weekKey: weekKey, weekLabel: finalLabel, dateLabel: `${formatD(weekStart)} - ${formatD(weekEnd)}`,
                                day: 0, night: 0, total: 0, otBadli: 0, otTypes: new Set(), isCurrentWeek: false,
                                dayEarn: 0, nightEarn: 0, otBadliEarn: 0, totalEarn: 0
                            });
                        }

                        const weekData = weeksMap.get(weekKey);
                        const localOffset = d.getTimezoneOffset() * 60000;
                        const dateStr = new Date(d.getTime() - localOffset).toISOString().split('T')[0];

                        if (dateStr === todayObj.toLocaleDateString('fr-CA')) weekData.isCurrentWeek = true;

                        const r = monthData[dateStr];
                        if (r) {
                            weekData.day += r.Day || 0; weekData.night += r.Night || 0; weekData.total += r.Total || 0; weekData.otBadli += r.otBadli || 0;
                            weekData.dayEarn += r.dayE || 0; weekData.nightEarn += r.nightE || 0; weekData.otBadliEarn += r.otBadliE || 0; weekData.totalEarn += r.totalE || 0;
                            if(r.otType) r.otType.split(' & ').forEach(t => weekData.otTypes.add(t));
                        }
                    }

                    Array.from(weeksMap.values()).forEach(w => {
                        resultRows.push({
                            isWeekRow: true, weekVal: w.weekKey, label: w.weekLabel, subLabel: w.dateLabel,
                            day: w.day, night: w.night, total: w.total, otBadli: w.otBadli, otType: Array.from(w.otTypes).join(' & '),
                            dayEarn: w.dayEarn, nightEarn: w.nightEarn, otBadliEarn: w.otBadliEarn, totalEarn: w.totalEarn,
                            isCurrent: w.isCurrentWeek
                        });
                    });
                }
            } else if (selectedYearForMonths) {
                const yearMonths = reports.month_wise[selectedYearForMonths] || {};
                const availableMonths = [];
                for(let i=0; i<12; i++) {
                    if (selectedYearForMonths === currentYearStr && i > todayObj.getMonth()) break;
                    availableMonths.push(ALL_MONTHS[i]);
                }
                availableMonths.forEach(monthName => {
                    const r = yearMonths[monthName] || { Day: 0, Night: 0, Total: 0, otBadli: 0, otType: "" };
                    resultRows.push({ 
                        isMonthRow: true, monthVal: monthName, label: monthName, subLabel: selectedYearForMonths, 
                        day: r.Day, night: r.Night, total: r.Total, otBadli: r.otBadli, otType: r.otType,
                        dayEarn: r.dayE, nightEarn: r.nightE, otBadliEarn: r.otBadliE, totalEarn: r.totalE,
                        isCurrent: selectedYearForMonths === currentYearStr && monthName === currentMonthStr 
                    });
                });
            } else {
                const availableYears = Object.keys(reports.year_wise).sort((a,b) => a-b);
                if (!availableYears.includes(currentYearStr)) availableYears.push(currentYearStr);
                
                availableYears.forEach(yearName => {
                    const r = reports.year_wise[yearName] || { Day: 0, Night: 0, Total: 0, otBadli: 0, otType: "" };
                    resultRows.push({ 
                        isYearRow: true, yearVal: yearName, label: yearName, subLabel: "Yearly Record", 
                        day: r.Day, night: r.Night, total: r.Total, otBadli: r.otBadli, otType: r.otType,
                        dayEarn: r.dayE, nightEarn: r.nightE, otBadliEarn: r.otBadliE, totalEarn: r.totalE,
                        isCurrent: yearName === currentYearStr 
                    });
                });
            }
        }
        return resultRows;
    }, [period, workerFilteredData, selectedYearForMonths, selectedMonthForDays, selectedWeekForDays, currentMonthTab, monthViewMode]);

    const handleBack = () => { 
        if(window.speechSynthesis) window.speechSynthesis.cancel();
        if (selectedDayForLooms) setSelectedDayForLooms(null); 
        else if (selectedWeekForDays) setSelectedWeekForDays(null); 
        else if (selectedMonthForDays) setSelectedMonthForDays(null); 
        else if (selectedYearForMonths) setSelectedYearForMonths(null); 
    };

    const isBackVisible = selectedYearForMonths !== null || selectedDayForLooms !== null || selectedWeekForDays !== null || selectedMonthForDays !== null;

    // --- SEARCH FILTER STATES ---
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [searchStartDate, setSearchStartDate] = useState('');
    const [searchEndDate, setSearchEndDate] = useState('');     
    const [selectedSearchLooms, setSelectedSearchLooms] = useState([]); 
    const [selectedShift, setSelectedShift] = useState('all');
    const [selectedWorkType, setSelectedWorkType] = useState('all');
    const [selectedDayOfWeek, setSelectedDayOfWeek] = useState('all');
    
    // Accordion Toggle States
    const [isDayDropdownOpen, setIsDayDropdownOpen] = useState(false);
    const [isWorkTypeDropdownOpen, setIsWorkTypeDropdownOpen] = useState(false);
    const [isLoomBoxOpen, setIsLoomBoxOpen] = useState(false); 

    const todayStrForStrip = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    let oldestDateStrForStrip = "1 January 2024";
    if (worker?.doj && worker.doj !== '0000-00-00') {
        const dojD = new Date(String(worker.doj).replace(/-/g, '/'));
        if (!isNaN(dojD)) oldestDateStrForStrip = dojD.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    }

    // 🌟 FILTERED REPORT DATA LOGIC 🌟
    const filteredReportData = useMemo(() => {
        if (!searchStartDate && !searchEndDate && selectedSearchLooms.length === 0 && selectedShift === 'all' && selectedWorkType === 'all' && selectedDayOfWeek === 'all') return reportData;

        const allDailyRows = [];
        const dailyWise = workerFilteredData.reports?.daily_wise || {};
        
        Object.entries(dailyWise).forEach(([year, months]) => {
            Object.entries(months).forEach(([month, days]) => {
                Object.entries(days).forEach(([dateStr, dayData]) => {
                    const d = new Date(dateStr);
                    allDailyRows.push({
                        rawDate: dateStr,
                        label: `${d.getDate()} ${d.toLocaleDateString('en-US', {month:'long'})}`,
                        subLabel: d.toLocaleDateString('en-US', {weekday:'long'}),
                        dayName: d.toLocaleDateString('en-US', {weekday:'long'}),
                        day: dayData.Day || 0,
                        night: dayData.Night || 0,
                        total: dayData.Total || 0,
                        otBadli: dayData.otBadli || 0,
                        otType: dayData.otType || "", 
                        otStartTime: dayData.otStartTime || "",
                        otEndTime: dayData.otEndTime || "",
                        looms: dayData.looms || {},
                        looms_ot: dayData.looms_ot || {},
                        dayEarn: dayData.dayE || 0,
                        nightEarn: dayData.nightE || 0,
                        otBadliEarn: dayData.otBadliE || 0,
                        totalEarn: dayData.totalE || 0,
                        isCurrent: dateStr === new Date().toLocaleDateString('fr-CA')
                    });
                });
            });
        });

        let result = allDailyRows;

        if (searchStartDate) result = result.filter(row => row.rawDate >= searchStartDate);
        if (searchEndDate) result = result.filter(row => row.rawDate <= searchEndDate);

        if (selectedDayOfWeek !== 'all') {
            result = result.filter(row => row.dayName && row.dayName.toLowerCase() === selectedDayOfWeek.toLowerCase());
        }

        if (selectedShift !== 'all') {
            result = result.filter(row => {
                if (selectedShift === 'day') return parseFloat(row.day || 0) > 0;
                if (selectedShift === 'night') return parseFloat(row.night || 0) > 0;
                return true;
            });
        }

        if (selectedWorkType !== 'all') {
            result = result.filter(row => {
                const typeStr = (row.otType || "").toUpperCase();
                const otBadliVal = parseFloat(row.otBadli || 0);
                const regularVal = parseFloat(row.day || 0) + parseFloat(row.night || 0);
                
                if (selectedWorkType === 'regular') return regularVal > 0;
                if (selectedWorkType === 'ot') return otBadliVal > 0 && (typeStr.includes('OT') || !typeStr.includes('BADLI'));
                if (selectedWorkType === 'badli') return otBadliVal > 0 && typeStr.includes('BADLI');
                return true;
            });
        }

        if (selectedSearchLooms.length > 0) {
            result = result.map(row => {
                let totalCombinedMeters = 0; let filteredLooms = {}; let filteredLoomsOt = {};
                selectedSearchLooms.forEach(lId => {
                    const loomMeters = parseFloat(row.looms[lId] || 0);
                    const loomOtMeters = parseFloat(row.looms_ot[lId] || 0);
                    if (loomMeters > 0) { totalCombinedMeters += loomMeters; filteredLooms[lId] = loomMeters; }
                    if (loomOtMeters > 0) { totalCombinedMeters += loomOtMeters; filteredLoomsOt[lId] = loomOtMeters; }
                });

                if (totalCombinedMeters > 0 || parseFloat(row.otBadli) > 0) {
                    let dayMeters = workerShift === 'Night' ? 0 : (workerShift === 'Day' ? totalCombinedMeters : totalCombinedMeters / 2);
                    let nightMeters = workerShift === 'Day' ? 0 : (workerShift === 'Night' ? totalCombinedMeters : totalCombinedMeters / 2);
                    const rate = parseFloat(myLiveApiData?.rate_per_meter || myLiveApiData?.ratePerMeter || 0);
                    
                    return { 
                        ...row, 
                        total: totalCombinedMeters, 
                        day: dayMeters, 
                        night: nightMeters, 
                        dayEarn: dayMeters * rate,         
                        nightEarn: nightMeters * rate,     
                        totalEarn: (totalCombinedMeters + parseFloat(row.otBadli || 0)) * rate,
                        looms: filteredLooms, 
                        looms_ot: filteredLoomsOt, 
                        otBadli: row.otBadli, 
                        otType: row.otType, 
                        otStartTime: row.otStartTime, 
                        otEndTime: row.otEndTime 
                    };
                }
                return null;
            }).filter(Boolean);
        }

        result.sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate));
        return result;
    }, [reportData, workerFilteredData, searchStartDate, searchEndDate, selectedSearchLooms, selectedShift, selectedWorkType, selectedDayOfWeek, workerShift, myLiveApiData]);
    
    const isAnyFilterActive = Boolean(
        searchStartDate || searchEndDate || selectedSearchLooms.length > 0 || 
        selectedShift !== 'all' || selectedWorkType !== 'all' || selectedDayOfWeek !== 'all'
    );
    
    useEffect(() => {
        if (selectedDayForLooms) {
            const liveUpdatedDay = filteredReportData.find(row => row.rawDate === selectedDayForLooms.rawDate);
            if (liveUpdatedDay) {
                setSelectedDayForLooms(liveUpdatedDay);
            } else {
                setSelectedDayForLooms(null); 
            }
        }
    }, [filteredReportData]);

    const activeHeaderInfo = useMemo(() => {
        const todayObj = new Date();
        const currentYearStr = todayObj.getFullYear().toString();
        const currentMonthStr = ALL_MONTHS[todayObj.getMonth()];

        if (selectedWeekForDays) {
            const wStart = new Date(selectedWeekForDays);
            const wEnd = new Date(wStart);
            wEnd.setDate(wStart.getDate() + 6);

            const yearVal = selectedYearForMonths || wStart.getFullYear().toString();
            const jan1 = new Date(parseInt(yearVal, 10), 0, 1);
            const jan1Diff = (jan1.getDay() + 1) % 7;
            const firstSat = new Date(jan1);
            firstSat.setDate(jan1.getDate() - jan1Diff);
            firstSat.setHours(0, 0, 0, 0);

            const weekNum = Math.floor((wStart - firstSat) / (7 * 24 * 60 * 60 * 1000)) + 1;
            const isTransition = (wStart.getMonth() === 11 && wEnd.getMonth() === 0);
            const mainTitle = isTransition ? "TRANSITION WEEK" : `WEEK ${weekNum}`;

            const startMonthName = ALL_MONTHS[wStart.getMonth()];
            const endMonthName = ALL_MONTHS[wEnd.getMonth()];
            const dateStripText = `${wStart.getDate()} ${startMonthName} TO ${wEnd.getDate()} ${endMonthName}`;

            return { mainTitle, dateStripText };
        } 
        else if (selectedMonthForDays) {
            const mainTitle = `${selectedMonthForDays.toUpperCase()} ${selectedYearForMonths || currentYearStr}`;
            const monthIndex = ALL_MONTHS.indexOf(selectedMonthForDays);
            const yearNum = parseInt(selectedYearForMonths || currentYearStr, 10);
            const daysInMonth = new Date(yearNum, monthIndex + 1, 0).getDate();

            let dateStripText = `1 ${selectedMonthForDays} TO ${daysInMonth} ${selectedMonthForDays}`;

            if (monthViewMode === 'weekly') {
                const firstDay = new Date(yearNum, monthIndex, 1);
                const diffToSatFirst = (firstDay.getDay() + 1) % 7;
                const firstWeekStart = new Date(firstDay);
                firstWeekStart.setDate(firstDay.getDate() - diffToSatFirst);

                const lastDay = new Date(yearNum, monthIndex, daysInMonth);
                const diffToSatLast = (lastDay.getDay() + 1) % 7;
                const lastWeekStart = new Date(lastDay);
                lastWeekStart.setDate(lastDay.getDate() - diffToSatLast);
                const lastWeekEnd = new Date(lastWeekStart);
                lastWeekEnd.setDate(lastWeekStart.getDate() + 6);

                const formatHeaderDt = (dt) => `${dt.getDate()} ${ALL_MONTHS[dt.getMonth()]}`;
                dateStripText = `${formatHeaderDt(firstWeekStart)} TO ${formatHeaderDt(lastWeekEnd)}`;
            }

            return { mainTitle, dateStripText };
        }
        else if (selectedYearForMonths) {
            const mainTitle = `${selectedYearForMonths}`;
            const endMonth = (selectedYearForMonths === currentYearStr) ? currentMonthStr : 'December';
            const dateStripText = `January TO ${endMonth}`;

            return { mainTitle, dateStripText };
        } 
       else {
            const mainTitle = ""; 
            const dateStripText = `${oldestDateStrForStrip} TO ${todayStrForStrip}`;

            return { mainTitle, dateStripText };
        }
    }, [selectedWeekForDays, selectedMonthForDays, selectedYearForMonths, oldestDateStrForStrip, todayStrForStrip, ALL_MONTHS, monthViewMode, worker]);

    // --- PDF GENERATION FUNCTION ---
    const handleGenerateReport = async (e) => {
        if (e) e.preventDefault();
        setIsDownloading(true);

        const safeFetch = async (url, options) => {
            let res;
            try {
                res = await fetch(url, options);
            } catch (err) {
                throw new Error(`Network Error: ${err.message}`);
            }
            const text = await res.text();
            if (!res.ok) throw new Error(`Server Error (${res.status})`);
            if (!text || text.trim() === "") throw new Error(`Blank Response from server`);
            try {
                return JSON.parse(text);
            } catch (err) {
                throw new Error(`Invalid JSON Response`);
            }
        };

        try {
            let pData = ownerProfile || {};
            try {
                let profileJson = await safeFetch(`${API_BASE}/get_owner_profile.php`);
                if (profileJson.status === 'success' && profileJson.data) {
                    pData = profileJson.data; 
                }
            } catch (err) {
                console.error("Owner profile fetch error:", err);
            }

            const safeStr = (val, fallback) => (val && String(val).trim() !== "null" && String(val).trim() !== "undefined" && String(val).trim() !== "") ? String(val).trim() : fallback;
            const biz = pData?.business_details || pData || {};
            const own = pData?.owner_details || pData || {};

            const TEXTILE_INFO = {
                name: safeStr(biz.textile_name, "MUKADAMPRO TEXTILES").toUpperCase(),
                owner: safeStr(own.owner_name, "OWNER NAME").toUpperCase(),
                mobile: safeStr(own.mobile, "-"),
                email: safeStr(own.email, "-"),
                gst: safeStr(biz.gstin, "-"),
                address: safeStr(biz.textile_address, "-"),
                totalLooms: safeStr(biz.total_looms, "0")
            };

            const rangeJson = await safeFetch(`${API_BASE}/search_api.php`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'date', start_date: reportDateStart, end_date: reportDateEnd })
            });
            const globalData = rangeJson.status === 'success' ? rangeJson.data : [];
            const getRowDate = (item) => item.date || item.entry_date || (item.created_at ? item.created_at.split(' ')[0] : '');
            const uniqueDates = [...new Set(globalData.map(getRowDate).filter(Boolean))].sort();

            const json = await safeFetch(`${API_BASE}/search_api.php`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'worker', start_date: reportDateStart, end_date: reportDateEnd, id: worker.id })
            });
            
            const workerLoomsMap = {}; 
            let workerTotal = 0; 
            let workerTotalEarn = 0;
            
            const dateLoomMap = {};
            const dateShiftMap = {};
            const dateRateMap = {};
            
            const sortedApiData = (json.data || []).sort((a, b) => new Date(getRowDate(a)) - new Date(getRowDate(b)));

            if (json.status === 'success' && sortedApiData.length > 0) {
                sortedApiData.forEach(r => {
                    const lId = r.loom_id || 'NA';
                    const meters = parseFloat(r.meters || r.total || r.production_meters || 0);
                    const earn = parseFloat(r.earning || r.amount || r.earnings || r.total_earning || 0);
                    const rDate = getRowDate(r);
                    
                    if (meters > 0) {
                        if (!dateLoomMap[rDate]) dateLoomMap[rDate] = new Set();
                        if (lId !== 'NA') dateLoomMap[rDate].add(parseInt(lId, 10));

                        if (!dateRateMap[rDate]) dateRateMap[rDate] = new Set();
                        let historicalRate = r.rate_per_meter || r.rate || r.ratePerMeter;
                        if (!historicalRate && earn > 0) historicalRate = (earn / meters).toFixed(2);
                        if (!historicalRate) historicalRate = worker?.rate_per_meter || worker?.rate || 0;
                        dateRateMap[rDate].add(Number(historicalRate).toFixed(2));

                        if (!dateShiftMap[rDate]) dateShiftMap[rDate] = new Set();
                        let sStr = r.shift || worker?.shift || '-';
                        let sStart = r.start_time || r.shift_start || worker?.shift_start || '00:00';
                        let sEnd = r.end_time || r.shift_end || worker?.shift_end || '00:00';                        
                        let sMins = parseTimeStr(sStart);
                        let eMins = parseTimeStr(sEnd);
                        let durationStr = "";
                        if (sMins !== undefined && eMins !== undefined) {
                            let diff = eMins - sMins;
                            if (diff < 0) diff += 1440;
                            const h = Math.floor(diff / 60);
                            const m = diff % 60;
                            durationStr = m > 0 ? `${h}h ${m}m` : `${h}h`;
                        }
                        dateShiftMap[rDate].add(`${sStr.toUpperCase()}\n${formatTimeForDisplay(sStart)} to ${formatTimeForDisplay(sEnd)}\n(${durationStr})`);
                    }

                    if (!workerLoomsMap[lId]) { workerLoomsMap[lId] = {}; uniqueDates.forEach(d => workerLoomsMap[lId][d] = 0); }
                    if (rDate && workerLoomsMap[lId][rDate] !== undefined) workerLoomsMap[lId][rDate] += meters;
                    workerTotal += meters;
                    workerTotalEarn += earn;
                });
            }

            const generateTimeline = (dateMap, formatItemFn) => {
                const sortedDates = Object.keys(dateMap).sort((a,b) => new Date(a) - new Date(b));
                if (sortedDates.length === 0) return null;
                
                let historyBlocks = [];
                let currentValStr = '';
                let startDate = sortedDates[0];
                let lastDate = sortedDates[0];

                const getValStr = (date) => {
                    let arr = Array.from(dateMap[date]);
                    if (arr.every(v => !isNaN(v))) arr.sort((a,b) => Number(a) - Number(b));
                    else arr.sort();
                    return arr.join(' | ');
                };

                currentValStr = getValStr(startDate);

                const formatDt = (d) => {
                    const dt = new Date(d);
                    return `${String(dt.getDate()).padStart(2, '0')} ${dt.toLocaleString('en-US', {month:'short'})} ${dt.getFullYear()}`;
                };

                for (let i = 1; i < sortedDates.length; i++) {
                    const date = sortedDates[i];
                    const valStr = getValStr(date);
                    
                    if (valStr !== currentValStr) {
                        let rangeStr = startDate === lastDate ? `DATE: ${formatDt(startDate)}` : `DATE: ${formatDt(startDate)} TO ${formatDt(lastDate)}`;
                        historyBlocks.push(`${rangeStr}\n${formatItemFn(currentValStr)}`);
                        currentValStr = valStr;
                        startDate = date;
                    }
                    lastDate = date;
                }
                
                if (historyBlocks.length === 0) {
                    return formatItemFn(currentValStr);
                } else {
                    let rangeStr = startDate === lastDate ? `DATE: ${formatDt(startDate)}` : `DATE: ${formatDt(startDate)} TO ${formatDt(lastDate)}`;
                    historyBlocks.push(`${rangeStr}\n${formatItemFn(currentValStr)}`);
                    return historyBlocks.join('\n\n'); 
                }
            };

            let rate = parseFloat(worker?.rate_per_meter || worker?.ratePerMeter || worker?.rate || 0); 

            const summaryTableBody = [];
            let overallTotalMeters = workerTotal;
            let overallTotalEarnings = workerTotalEarn > 0 ? workerTotalEarn : (overallTotalMeters * rate);

            const workerNameStr = worker?.name ? worker.name.toUpperCase() : 'UNKNOWN WORKER';
            const workerMobileStr = worker?.mobile || worker?.phone || 'N/A';
            const workerAddressStr = worker?.address || 'N/A';
            const workerInfoCell = `${workerNameStr}\nMobile: ${workerMobileStr}\nAddress: ${workerAddressStr}`;

            const shiftInfoCell = generateTimeline(dateShiftMap, val => val) || `${worker?.shift ? worker.shift.toUpperCase() : '-'}\n${formatTimeForDisplay(worker?.shift_start)} TO ${formatTimeForDisplay(worker?.shift_end)}`;

            const defaultLoomsArr = worker?.looms ? String(worker.looms).split(',').filter(x => x.trim() !== '') : [];
            const loomsInfoCell = generateTimeline(dateLoomMap, val => {
                if(!val) return '-';
                const arr = val.split(' | ').map(Number).sort((a,b)=>a-b);
                return `Total: ${arr.length}\n[ ${arr.join(', ')} ]`;
            }) || `Total: ${defaultLoomsArr.length}\n[ ${defaultLoomsArr.join(', ') || '-'} ]`;

            const ratesInfoCell = generateTimeline(dateRateMap, val => {
                if(!val) return '-';
                return val.split(' | ').map(r => `Rs. ${r}`).join('\n');
            }) || `Rs. ${rate.toFixed(2)}`;

            summaryTableBody.push([
                workerInfoCell,
                shiftInfoCell,
                loomsInfoCell,
                ratesInfoCell,
                `${overallTotalMeters.toFixed(1)}\nMeter`,
                `Rs. ${overallTotalEarnings.toFixed(0)}`
            ]);

            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const centerX = 297 / 2;

            const drawWatermark = () => {
                doc.setTextColor(240, 245, 250); 
                doc.setFontSize(45);
                doc.setFont("helvetica", "bold");
                doc.text(TEXTILE_INFO.name, centerX, 110, { align: "center", angle: 35 });
            };

            drawWatermark();

            const originalAddPage = doc.addPage.bind(doc);
            doc.addPage = function(...args) {
                originalAddPage(...args);
                drawWatermark(); 
                return this;     
            };

            doc.setFont("helvetica", "bold");
            doc.setFontSize(16);
            doc.setTextColor(15, 23, 42); 
            doc.text("WORKER PRODUCTION REPORT", centerX, 15, { align: "center" });

            const downloadTime = new Date().toLocaleString('en-IN', { 
                day: 'numeric', month: 'short', year: 'numeric', 
                hour: '2-digit', minute: '2-digit', hour12: true 
            });
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(100, 116, 139);
            doc.text(`Downloaded: ${downloadTime}`, 297 - 14, 15, { align: "right" });

            doc.setFontSize(20);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(51, 65, 85);
            doc.text(TEXTILE_INFO.name, centerX, 24, { align: "center" });

            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(100, 116, 139);

            doc.text(`Owner: ${TEXTILE_INFO.owner}   |   Total Looms: ${TEXTILE_INFO.totalLooms}   |   Mobile: ${TEXTILE_INFO.mobile}`, centerX, 30, { align: "center" });
            doc.text(`Email: ${TEXTILE_INFO.email}   |   GST No: ${TEXTILE_INFO.gst}`, centerX, 35, { align: "center" });
            doc.text(`Address: ${TEXTILE_INFO.address}`, centerX, 40, { align: "center" });

            doc.setDrawColor(226, 232, 240); 
            doc.setLineWidth(0.5);
            doc.line(14, 44, 297 - 14, 44);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.setTextColor(15, 23, 42);
            
            const dateRangeText = `Period: ${reportDateStart} TO ${reportDateEnd}`;
            doc.text(`Summary: ${worker?.name ? worker.name.toUpperCase() : 'WORKER'}`, centerX, 50, { align: "center" }); 
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.text(dateRangeText, centerX, 55, { align: "center" });

            autoTable(doc, {
                head: [['Worker Details', 'Shift & Time', 'Looms Assigned', 'Rate History', 'Total Prod.', 'Total Earning']],
                body: summaryTableBody,
                startY: 60, 
                theme: 'grid',
                headStyles: { fillColor: [15, 23, 42], textColor: 255, fontSize: 9, halign: 'center', valign: 'middle' },
                alternateRowStyles: { fillColor: [247, 247, 247] },
                styles: { fillColor: [255, 255, 255], fontSize: 8.5, halign: 'center', valign: 'middle', cellPadding: 4, textColor: [15, 23, 42] },
                columnStyles: { 0: { halign: 'left', fontStyle: 'normal' } }, 
                margin: { left: 14, right: 14 }
            });

            doc.save(`${worker?.name || 'Worker'}_production_report.pdf`);
            setShowReportModal(false);
        } catch (err) { 
            alert("Download Failed: " + err.message); 
        } finally { 
            setIsDownloading(false); 
        }
    };

    if (loading) {
        return (
            <div style={{
                zoom: 0.95, fontSize: '14px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                zIndex: 100, fontFamily: "'Plus Jakarta Sans', sans-serif", 
                background: appBgFinal
            }}>
                <div style={{textAlign:'center', color:'#111', fontWeight: '600', fontSize:'12px'}}>
                    Loading worker history details...
                </div>
            </div>
        );
    }

   return (
    <div style={{
        zoom: 0.95, fontSize: '14px',
        position: 'fixed', top: 0, left: 0, right: 0, bottom: '80px',
        overflow: 'hidden', display: 'flex', flexDirection: 'column', zIndex: 90, 
        fontFamily: "'Plus Jakarta Sans', sans-serif", 
        background: appBgFinal, WebkitOverflowScrolling: 'touch', transition: 'background 0.3s ease'
    }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
                * { font-family: 'Plus Jakarta Sans', sans-serif; box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
                ::-webkit-scrollbar { width: 4px; height: 4px; }
                ::-webkit-scrollbar-track { background: transparent; }
                ::-webkit-scrollbar-thumb { background: rgba(15, 23, 42, 0.15); border-radius: 10px; }
                
                .forced-white-badge { color: #ffffff !important; }
                @keyframes slideUpFade { 0% { opacity: 0; transform: translateY(15px); } 100% { opacity: 1; transform: translateY(0); } }
                .animate-in { animation: slideUpFade 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
            `}</style>

           {/* 🌟 DARK BLUE PREMIUM HEADER BAR 🌟 */}
            <div style={{ 
                background: '#0F172A', 
                padding: '14px 16px', 
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)', 
                display: 'flex', alignItems: 'center', 
                position: 'sticky', top: 0, zIndex: 100000, 
                width: '100%', boxSizing: 'border-box', minHeight: '64px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}>
                
                {/* Back Button */}
                <div style={{ position: 'relative', zIndex: 20 }}>
                    <button onClick={(e) => {
                        const action = isBackVisible ? handleBack : (onBack || (() => window.history.back()));
                        handleVoiceClick ? handleVoiceClick(e, "बैक जा रहे हैं।", action) : action();
                    }} style={{ background: '#1E293B', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                        <ArrowLeft size={18} color="#FFFFFF" strokeWidth={2.5}/>
                    </button>
                </div>

                {selectedDayForLooms ? (
                    <div style={{ position: 'absolute', left: 0, right: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', zIndex: 10 }}>
                        <button onClick={() => { const i = filteredReportData.findIndex(r => r.rawDate === selectedDayForLooms.rawDate); if (i > 0) setSelectedDayForLooms(filteredReportData[i - 1]); }} style={{ background: '#1E293B', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '50%', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: filteredReportData.findIndex(r => r.rawDate === selectedDayForLooms.rawDate) > 0 ? 1 : 0.3 }} disabled={filteredReportData.findIndex(r => r.rawDate === selectedDayForLooms.rawDate) <= 0}>
                            <ChevronLeft size={18} color="#FFFFFF" strokeWidth={2.5} />
                        </button>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center', minWidth: '120px' }}>
                            <h1 style={{ fontSize: '15px', fontWeight: '900', color: '#FFFFFF', margin: 0, letterSpacing: '-0.3px', textTransform: 'uppercase', textAlign: 'center' }}>{selectedDayForLooms.label}</h1>
                            <span style={{ fontSize: '10px', color: '#94A3B8', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase', textAlign: 'center' }}>{selectedDayForLooms.subLabel}</span>
                        </div>
                        <button onClick={() => { const i = filteredReportData.findIndex(r => r.rawDate === selectedDayForLooms.rawDate); if (i !== -1 && i < filteredReportData.length - 1) setSelectedDayForLooms(filteredReportData[i + 1]); }} style={{ background: '#1E293B', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '50%', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: filteredReportData.findIndex(r => r.rawDate === selectedDayForLooms.rawDate) !== -1 && filteredReportData.findIndex(r => r.rawDate === selectedDayForLooms.rawDate) < filteredReportData.length - 1 ? 1 : 0.3 }} disabled={filteredReportData.findIndex(r => r.rawDate === selectedDayForLooms.rawDate) === -1 || filteredReportData.findIndex(r => r.rawDate === selectedDayForLooms.rawDate) >= filteredReportData.length - 1}>
                            <ChevronRight size={18} color="#FFFFFF" strokeWidth={2.5} />
                        </button>
                    </div>
               ) : (
                    /* Worker Profile Title Info in Header */
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: '12px', zIndex: 20, flex: 1 }}>
                        <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: '#1E293B', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.15)', overflow: 'hidden', flexShrink: 0 }}>
                            {worker?.image || worker?.profile_image ? (
                                <img src={worker.image || worker.profile_image} alt={worker?.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                                <User size={18} color="#FFFFFF" />
                            )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                            <h1 style={{ fontSize: '14px', fontWeight: '900', color: '#FFFFFF', margin: 0, letterSpacing: '-0.3px', textTransform: 'uppercase' }}>
                                {worker?.name || "WORKER HISTORY"}
                            </h1>
                            <span style={{ fontSize: '9px', color: '#38BDF8', fontWeight: '800', letterSpacing: '0.4px', textTransform: 'uppercase', whiteSpace: 'normal', maxWidth: 'calc(100vw - 160px)', lineHeight: '1.4' }}>
                                {getWorkerLoomsHeaderStr(worker)}
                            </span>
                        </div>
                    </div>
                )}

                {/* Header Action Icons */}
                <div style={{ position: 'absolute', right: '16px', zIndex: 20, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    
                    {/* TUTORIAL / SMART GUIDE ICON */}
                    <button 
                        onClick={(e) => handleVoiceClick ? handleVoiceClick(e, " ऐप चलाने की लाइव ट्रेनिंग लेने के लिए स्मार्ट गाइड मेनू पर क्लिक करें।", () => setShowGuideModal(true), 'btn-guide-history') : setShowGuideModal(true)} 
                        style={{ background: '#1E293B', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                        <HelpCircle size={18} color="#38BDF8" strokeWidth={2.5}/>
                    </button>

                    {/* Search Toggle Icon */}
                    <button 
                        onClick={() => { 
                            setIsSearchOpen(!isSearchOpen); 
                            setIsMenuOpen(false);
                            if(isSearchOpen) { setSearchStartDate(''); setSearchEndDate(''); setSelectedSearchLooms([]); setSelectedWorkType('all'); setIsLoomBoxOpen(false); } 
                        }}
                        style={{ background: isSearchOpen ? 'rgba(56, 189, 248, 0.2)' : '#1E293B', borderRadius: '12px', border: isSearchOpen ? '1px solid #38BDF8' : '1px solid rgba(255,255,255,0.12)', cursor: 'pointer', padding: '8px', display:'flex', alignItems:'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                        {isSearchOpen ? <X size={18} color="#38BDF8" strokeWidth={2.5} /> : <Search size={18} color="#FFFFFF" strokeWidth={2.5}/>}
                    </button>

                    {/* Menu Icon */}
                    <div style={{ position: 'relative' }}>
                        <button 
                            onClick={() => { setIsMenuOpen(!isMenuOpen); setIsSearchOpen(false); }}
                            style={{ background: isMenuOpen ? '#334155' : '#1E293B', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer', padding: '8px', display:'flex', alignItems:'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                            <Menu size={18} color="#FFFFFF" strokeWidth={2.5}/>
                        </button>

                        {isMenuOpen && (
                            <div style={{ position: 'absolute', top: '120%', right: 0, background: '#1E293B', borderRadius: '14px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.12)', overflow: 'hidden', minWidth: '180px', zIndex: 100, display: 'flex', flexDirection: 'column' }}>
                                <div onClick={(e) => { setIsMenuOpen(false); setIsEditModalOpen(true); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.08)', background: '#1E293B' }}>
                                    <Edit size={16} color="#94A3B8" strokeWidth={2.5} />
                                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#FFFFFF' }}>Edit Worker</span>
                                </div>
                                <div 
    onClick={(e) => { 
        e.stopPropagation();
        setIsMenuOpen(false); 
        setShowReportModal(true); 
        if (typeof handleVoiceClick === 'function') {
            try {
                handleVoiceClick(e, "रिपोर्ट डाउनलोड करने के ऑप्शन खुल रहे हैं।", () => {});
            } catch (err) {
                console.log("Voice assist bypass:", err);
            }
        }
    }} 
    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', cursor: 'pointer', background: '#0F172A' }}
>
    <Download size={16} color="#38BDF8" strokeWidth={2.5} />
    <span style={{ fontSize: '13px', fontWeight: '800', color: '#38BDF8' }}>Download Report</span>
</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 🌟 SEARCH CONTROL PANEL 🌟 */}
            {isSearchOpen && (
                <div className="animate-in" style={{
                    background: '#0F172A',
                    padding: '18px 16px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
                    position: 'sticky', top: '64px', zIndex: 99,
                    boxShadow: '0 30px 60px rgba(0, 0, 0, 0.6)',
                    borderBottomLeftRadius: '24px', borderBottomRightRadius: '24px',
                    display: 'flex', flexDirection: 'column', gap: '16px',
                    color: '#FFFFFF'
                }}>
                    
                    {/* Header Title Bar */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ background: '#38BDF8', width: '8px', height: '8px', borderRadius: '50%', boxShadow: '0 0 10px #38BDF8' }}></div>
                            <span style={{ fontSize: '11px', fontWeight: '900', letterSpacing: '1px', color: '#FFFFFF', textTransform: 'uppercase' }}>
                                QUICK PRODUCTION FILTER
                            </span>
                        </div>
                        {isAnyFilterActive && (
                            <span style={{ fontSize: '9px', fontWeight: '900', background: 'rgba(56, 189, 248, 0.2)', color: '#38BDF8', border: '1px solid rgba(56, 189, 248, 0.4)', padding: '2px 8px', borderRadius: '20px' }}>
                                FILTER APPLIED
                            </span>
                        )}
                    </div>

                    {/* 1. HERO SECTION: DATE RANGE TIMELINE */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span style={{ fontSize: '9px', fontWeight: '900', color: '#38BDF8', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                            SELECT DATE RANGE
                        </span>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '8px', alignItems: 'center', background: '#1E293B', padding: '10px 14px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <span style={{ fontSize: '8px', fontWeight: '900', color: '#38BDF8', textTransform: 'uppercase' }}>FROM DATE</span>
                                <input 
                                    type="date" 
                                    value={searchStartDate} 
                                    onChange={(e) => setSearchStartDate(e.target.value)} 
                                    style={{
                                        width: '100%', background: 'transparent', border: 'none',
                                        color: '#FFFFFF', fontSize: '12px', fontWeight: '800', outline: 'none',
                                        colorScheme: 'dark'
                                    }} 
                                />
                            </div>

                            <div style={{ background: '#0F172A', padding: '6px 10px', borderRadius: '10px', fontSize: '11px', color: '#38BDF8', fontWeight: '900', border: '1px solid rgba(255,255,255,0.08)' }}>
                                ➔
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'right' }}>
                                <span style={{ fontSize: '8px', fontWeight: '900', color: '#F43F5E', textTransform: 'uppercase' }}>TO DATE</span>
                                <input 
                                    type="date" 
                                    value={searchEndDate} 
                                    onChange={(e) => setSearchEndDate(e.target.value)} 
                                    style={{
                                        width: '100%', background: 'transparent', border: 'none',
                                        color: '#FFFFFF', fontSize: '12px', fontWeight: '800', outline: 'none',
                                        textAlign: 'right', colorScheme: 'dark'
                                    }} 
                                />
                            </div>
                        </div>
                    </div>

                    {/* 2. SHIFT & WORK CATEGORY SWITCHERS */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <span style={{ fontSize: '9px', fontWeight: '800', color: '#94A3B8', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                                SHIFT
                            </span>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', background: '#1E293B', padding: '3px', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)' }}>
                                {[
                                    { label: 'ALL', val: 'all' },
                                    { label: 'DAY', val: 'day' },
                                    { label: 'NIGHT', val: 'night' }
                                ].map(s => {
                                    const active = selectedShift === s.val;
                                    return (
                                        <button
                                            key={s.val}
                                            onClick={() => setSelectedShift(s.val)}
                                            style={{
                                                padding: '8px 2px', borderRadius: '10px', border: 'none',
                                                fontSize: '9px', fontWeight: '900', cursor: 'pointer',
                                                background: active ? 'linear-gradient(135deg, #0284C7 0%, #38BDF8 100%)' : 'transparent',
                                                color: active ? '#FFFFFF' : '#94A3B8',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            {s.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <span style={{ fontSize: '9px', fontWeight: '800', color: '#94A3B8', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                                WORK TYPE
                            </span>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', background: '#1E293B', padding: '3px', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.08)' }}>
                                {[
                                    { label: 'ALL', val: 'all' },
                                    { label: 'REGULAR', val: 'regular' },
                                    { label: 'OVERTIME', val: 'ot' },
                                    { label: 'BADLI', val: 'badli' }
                                ].map(w => {
                                    const active = selectedWorkType === w.val;
                                    return (
                                        <button
                                            key={w.val}
                                            onClick={() => setSelectedWorkType(w.val)}
                                            style={{
                                                padding: '8px 2px', borderRadius: '10px', border: 'none',
                                                fontSize: '8px', fontWeight: '900', cursor: 'pointer',
                                                background: active ? 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)' : 'transparent',
                                                color: active ? '#FFFFFF' : '#94A3B8',
                                                transition: 'all 0.2s ease', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                                            }}
                                        >
                                            {w.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* 3. OPTIONAL ACCORDION: LOOMS SELECTION */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <button
                            onClick={() => setIsLoomBoxOpen(!isLoomBoxOpen)}
                            style={{
                                width: '100%', padding: '10px 12px', borderRadius: '12px',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                background: isLoomBoxOpen ? 'rgba(56, 189, 248, 0.15)' : '#1E293B',
                                color: isLoomBoxOpen ? '#38BDF8' : '#FFFFFF',
                                fontSize: '9px', fontWeight: '800', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer',
                                transition: 'all 0.2s ease', letterSpacing: '0.2px'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '9px', fontWeight: '800', color: isLoomBoxOpen ? '#38BDF8' : '#FFFFFF' }}>
                                    {selectedSearchLooms.length > 0 
                                        ? `LOOMS FILTERED (${selectedSearchLooms.length} SELECTED)` 
                                        : 'FILTER SPECIFIC LOOMS (DEFAULT: ALL LOOMS)'}
                                </span>
                            </div>
                            <ChevronDown size={12} style={{ transform: isLoomBoxOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }} />
                        </button>

                        {isLoomBoxOpen && (
                            <div className="animate-in" style={{
                                background: '#1E293B', padding: '12px', borderRadius: '16px',
                                border: '1px solid rgba(56, 189, 248, 0.3)', display: 'flex', flexDirection: 'column', gap: '10px'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px dashed rgba(255,255,255,0.1)', paddingBottom: '6px' }}>
                                    <span style={{ fontSize: '9px', fontWeight: '900', color: '#94A3B8' }}>TAP TO CHOOSE LOOMS</span>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button onClick={() => setSelectedSearchLooms(workerLooms.map(l => l.loom_id))} style={{ fontSize: '9px', fontWeight: '900', color: '#38BDF8', background: 'transparent', border: 'none', cursor: 'pointer' }}>SELECT ALL</button>
                                        {selectedSearchLooms.length > 0 && (
                                            <button onClick={() => setSelectedSearchLooms([])} style={{ fontSize: '9px', fontWeight: '900', color: '#F43F5E', background: 'transparent', border: 'none', cursor: 'pointer' }}>RESET</button>
                                        )}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '110px', overflowY: 'auto' }}>
                                    {workerLooms.map(l => {
                                        const isSelected = selectedSearchLooms.includes(l.loom_id);
                                        return (
                                            <button 
                                                key={l.loom_id}
                                                onClick={() => setSelectedSearchLooms(prev => prev.includes(l.loom_id) ? prev.filter(id => id !== l.loom_id) : [...prev, l.loom_id])}
                                                style={{
                                                    padding: '6px 12px', borderRadius: '10px', fontSize: '10px', fontWeight: '900',
                                                    background: isSelected ? 'linear-gradient(135deg, #0284C7 0%, #38BDF8 100%)' : 'rgba(255,255,255,0.06)',
                                                    color: isSelected ? '#FFFFFF' : '#94A3B8',
                                                    cursor: 'pointer', border: isSelected ? '1px solid #38BDF8' : '1px solid rgba(255,255,255,0.08)',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                Loom {String(l.loom_id).padStart(2, '0')}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 4. OPTIONAL ACCORDION: DAY OF WEEK */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <button
                            onClick={() => setIsDayDropdownOpen(!isDayDropdownOpen)}
                            style={{
                                width: '100%', padding: '10px 12px', borderRadius: '12px',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                background: isDayDropdownOpen ? 'rgba(52, 211, 153, 0.15)' : '#1E293B',
                                color: isDayDropdownOpen ? '#34D399' : '#FFFFFF',
                                fontSize: '9px', fontWeight: '800', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer',
                                transition: 'all 0.2s ease', letterSpacing: '0.2px'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '9px', fontWeight: '800', color: isDayDropdownOpen ? '#34D399' : '#FFFFFF' }}>
                                    {selectedDayOfWeek !== 'all' 
                                        ? `DAY FILTERED (${selectedDayOfWeek.toUpperCase()})` 
                                        : 'FILTER SPECIFIC DAY OF WEEK (DEFAULT: ALL DAYS)'}
                                </span>
                            </div>
                            <ChevronDown size={12} style={{ transform: isDayDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }} />
                        </button>

                        {isDayDropdownOpen && (
                            <div className="animate-in" style={{
                                background: '#1E293B', padding: '12px', borderRadius: '16px',
                                border: '1px solid rgba(52, 211, 153, 0.3)', display: 'flex', gap: '6px', overflowX: 'auto', scrollbarWidth: 'none'
                            }}>
                                {[
                                    { label: 'ALL DAYS', val: 'all' },
                                    { label: 'MON', val: 'Monday' },
                                    { label: 'TUE', val: 'Tuesday' },
                                    { label: 'WED', val: 'Wednesday' },
                                    { label: 'THU', val: 'Thursday' },
                                    { label: 'FRI', val: 'Friday' },
                                    { label: 'SAT', val: 'Saturday' },
                                    { label: 'SUN', val: 'Sunday' }
                                ].map(d => {
                                    const active = selectedDayOfWeek.toLowerCase() === d.val.toLowerCase();
                                    return (
                                        <button
                                            key={d.val}
                                            onClick={() => setSelectedDayOfWeek(d.val)}
                                            style={{
                                                padding: '8px 12px', borderRadius: '10px', border: active ? '1px solid #34D399' : '1px solid rgba(255,255,255,0.1)',
                                                fontSize: '10px', fontWeight: '900', cursor: 'pointer', whiteSpace: 'nowrap',
                                                background: active ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.05)',
                                                color: active ? '#34D399' : '#CBD5E1',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            {d.label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* 5. ACTION BUTTONS */}
                    <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                        <button 
                            onClick={() => { setIsSearchOpen(false); setIsLoomBoxOpen(false); setIsDayDropdownOpen(false); }} 
                            style={{
                                flex: 1,
                                background: 'linear-gradient(135deg, #0284C7 0%, #38BDF8 100%)',
                                color: '#FFFFFF', border: 'none', padding: '14px', borderRadius: '16px',
                                fontSize: '12px', fontWeight: '900', cursor: 'pointer', letterSpacing: '0.8px',
                                boxShadow: '0 8px 20px rgba(2, 132, 199, 0.4)', transition: 'all 0.2s ease'
                            }}
                        >
                            APPLY & CLOSE
                        </button>

                        <button 
                            onClick={() => { setSearchStartDate(''); setSearchEndDate(''); setSelectedSearchLooms([]); setSelectedShift('all'); setSelectedWorkType('all'); setSelectedDayOfWeek('all'); }} 
                            style={{
                                background: 'rgba(244, 63, 94, 0.15)',
                                color: '#F43F5E', border: '1px solid rgba(244, 63, 94, 0.3)', padding: '14px 20px', borderRadius: '16px',
                                fontSize: '12px', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}
                        >
                            <X size={18} strokeWidth={3} />
                        </button>
                    </div>

                </div>
            )}

            {/* 🌟 DYNAMIC ACTIVE FILTER BADGES STRIP 🌟 */}
            {isAnyFilterActive && (
                <div className="animate-in" style={{
                    background: '#0B1329',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justify: 'space-between',
                    gap: '10px',
                    position: 'sticky',
                    top: isSearchOpen ? 'auto' : '64px',
                    zIndex: 8,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflowX: 'auto', scrollbarWidth: 'none' }}>
                        <span style={{ fontSize: '10px', fontWeight: '900', color: '#94A3B8', letterSpacing: '1px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                            FILTERS:
                        </span>

                        {(searchStartDate || searchEndDate) && (
                            <span style={{ 
                                fontSize: '11px', fontWeight: '800', background: 'rgba(2, 132, 199, 0.2)', color: '#38BDF8', 
                                padding: '4px 12px', borderRadius: '20px', border: '1px solid #0284C7', whiteSpace: 'nowrap', 
                                display: 'inline-flex', alignItems: 'center', gap: '6px' 
                            }}>
                                📅 {searchStartDate || 'START'} ➔ {searchEndDate || 'END'}
                            </span>
                        )}

                        {selectedDayOfWeek !== 'all' && (
                            <span style={{ fontSize: '11px', fontWeight: '800', background: 'rgba(99, 102, 241, 0.2)', color: '#A5B4FC', padding: '4px 12px', borderRadius: '20px', border: '1px solid #6366F1', whiteSpace: 'nowrap' }}>
                                📆 {selectedDayOfWeek.toUpperCase()}
                            </span>
                        )}

                        {selectedShift !== 'all' && (
                            <span style={{ fontSize: '11px', fontWeight: '800', background: 'rgba(234, 88, 12, 0.2)', color: '#FDBA74', padding: '4px 12px', borderRadius: '20px', border: '1px solid #EA580C', whiteSpace: 'nowrap' }}>
                                ⏱️ {selectedShift.toUpperCase()} SHIFT
                            </span>
                        )}

                        {selectedWorkType !== 'all' && (
                            <span style={{ fontSize: '11px', fontWeight: '800', background: 'rgba(16, 185, 129, 0.2)', color: '#6EE7B7', padding: '4px 12px', borderRadius: '20px', border: '1px solid #059669', whiteSpace: 'nowrap' }}>
                                ⚡ {selectedWorkType === 'ot' ? 'OVERTIME' : selectedWorkType.toUpperCase()}
                            </span>
                        )}

                        {selectedSearchLooms.length > 0 && (
                            <span style={{ fontSize: '11px', fontWeight: '800', background: 'rgba(219, 39, 119, 0.2)', color: '#F472B6', padding: '4px 12px', borderRadius: '20px', border: '1px solid #DB2777', whiteSpace: 'nowrap' }}>
                                🧵 {selectedSearchLooms.length} LOOMS ({selectedSearchLooms.map(id => `L-${id}`).join(',')})
                            </span>
                        )}
                    </div>

                    <button 
                        onClick={() => {
                            setSearchStartDate('');
                            setSearchEndDate('');
                            setSelectedSearchLooms([]);
                            setSelectedShift('all');
                            setSelectedWorkType('all');
                            setSelectedDayOfWeek('all');
                        }}
                        style={{
                            background: '#FFF1F2',
                            color: '#E11D48',
                            border: '1px solid #FECDD3',
                            padding: '5px 12px',
                            borderRadius: '8px',
                            fontSize: '10px',
                            fontWeight: '900',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 6px rgba(225, 29, 72, 0.15)'
                        }}
                    >
                        RESET <X size={12} strokeWidth={3} />
                    </button>
                </div>
            )}

           {/* 🌟 DARK BLUE PREMIUM CONTAINER BOX (SCREENSHOT MATCHED UI) 🌟 */}
<div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '12px 14px 12px 14px', boxSizing: 'border-box', minHeight: 0, overflow: 'hidden' }}>
    <div className="animate-in" style={{ 
        maxHeight: '100%', 
        background: '#0F172A', 
        border: '1px solid rgba(255, 255, 255, 0.12)', 
        borderRadius: '24px', 
        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)', 
        overflow: 'hidden', 
        padding: 0, 
        display: 'flex', 
        flexDirection: 'column' 
    }}>
        {selectedDayForLooms ? (
            /* 🌟 DEEP DIVE LOOMS DETAIL VIEW (DARK BLUE THEME) 🌟 */
            <div style={{ padding: '16px', overflowY: 'auto', flex: 1, background: '#0B1329', color: '#FFFFFF' }}>
                
                {/* DUTY TIME & RATE / METER BANNERS */}
                {(() => {
                    const hasRegularDay = parseFloat(selectedDayForLooms?.day || 0) > 0;
                    const hasRegularNight = parseFloat(selectedDayForLooms?.night || 0) > 0;
                    const hasRegularLooms = selectedDayForLooms?.looms && Object.values(selectedDayForLooms.looms).some(m => parseFloat(m) > 0);
                    const hasRegularProd = hasRegularDay || hasRegularNight || hasRegularLooms;

                    return (
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: '1.5fr 1fr', 
                            gap: '8px', 
                            marginBottom: '14px',
                            opacity: hasRegularProd ? 1 : 0.35,
                            filter: hasRegularProd ? 'none' : 'grayscale(1)',
                            pointerEvents: hasRegularProd ? 'auto' : 'none',
                            transition: 'all 0.3s ease'
                        }}>
                        
                        {/* Duty Time Box */}
                        <div style={{ 
                            background: '#1E293B', 
                            padding: '10px 12px', 
                            borderRadius: '14px', 
                            display: 'flex', alignItems: 'center', gap: '10px', 
                            border: '1px solid rgba(255,255,255,0.1)',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                            flex: 1
                        }}>
                            <div style={{ 
                                background: 'rgba(56, 189, 248, 0.15)', 
                                padding: '8px', borderRadius: '10px', 
                                border: '1px solid rgba(56, 189, 248, 0.3)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <Clock size={18} color="#38BDF8" strokeWidth={2.5} />
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%', minWidth: 0 }}>
                                {myLiveApiData?.shift_start && myLiveApiData?.shift_end ? (
                                    (() => {
                                        const sMins = parseTimeStr(myLiveApiData.shift_start);
                                        const eMins = parseTimeStr(myLiveApiData.shift_end);
                                        let diff = eMins - sMins;
                                        if (diff < 0) diff += 1440;
                                        const h = Math.floor(diff / 60);
                                        const m = diff % 60;
                                        const durStr = m > 0 ? `${h}:${String(m).padStart(2, '0')} hrs` : `${h} hrs`;

                                        return (
                                            <>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                                    <span style={{ fontSize: '9px', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                                                        DUTY TIME
                                                    </span>
                                                    <span style={{ 
                                                        fontSize: '9px', fontWeight: '900', color: '#38BDF8', 
                                                        background: 'rgba(56, 189, 248, 0.15)', padding: '2px 7px', 
                                                        borderRadius: '6px', border: '1px solid rgba(56, 189, 248, 0.3)',
                                                        letterSpacing: '0.3px', whiteSpace: 'nowrap'
                                                    }}>
                                                        {durStr}
                                                    </span>
                                                </div>

                                                <div style={{ fontSize: '13px', fontWeight: '900', color: '#FFFFFF', letterSpacing: '-0.2px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    {formatTimeForDisplay(myLiveApiData.shift_start)}
                                                    <span style={{ fontSize: '9px', color: '#38BDF8', fontWeight: '800' }}>TO</span>
                                                    {formatTimeForDisplay(myLiveApiData.shift_end)}
                                                </div>
                                            </>
                                        );
                                    })()
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <span style={{ fontSize: '9px', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase' }}>
                                            DUTY TIME
                                        </span>
                                        <span style={{ fontSize: '12px', fontWeight: '900', color: '#64748B' }}>
                                            NOT SET
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Rate Box */}
                        <div style={{ 
                            background: '#1E293B', 
                            padding: '10px 12px', 
                            borderRadius: '14px', 
                            display: 'flex', alignItems: 'center', gap: '10px', 
                            border: '1px solid rgba(255,255,255,0.1)',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
                        }}>
                            <div style={{ 
                                background: 'rgba(52, 211, 153, 0.15)', 
                                padding: '8px', borderRadius: '10px', 
                                border: '1px solid rgba(52, 211, 153, 0.3)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <IndianRupee size={18} color="#34D399" strokeWidth={2.5} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <span style={{ fontSize: '10px', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                                    RATE / METER
                                </span>
                                <span style={{ fontSize: '15px', fontWeight: '900', color: '#34D399', letterSpacing: '-0.2px' }}>
                                    ₹{myLiveApiData?.rate_per_meter || myLiveApiData?.ratePerMeter || 0}
                                </span>
                            </div>
                        </div>
                    </div>
                    );
                })()}

                {/* Regular Looms Grid */}
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <div style={{ height: '1px', flex: 1, background: 'rgba(255,255,255,0.1)' }}></div>
                        <span style={{ fontSize: '10px', fontWeight: '800', color: '#94A3B8', letterSpacing: '1px', textTransform: 'uppercase' }}>REGULAR PRODUCTION</span>
                        <div style={{ height: '1px', flex: 1, background: 'rgba(255,255,255,0.1)' }}></div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                        {Object.entries(selectedDayForLooms.looms).map(([lId, meters]) => {
                            if(parseFloat(meters) <= 0) return null;
                            const loomIdLabel = String(lId).padStart(2, '0');
                            const isNight = workerShift === 'Night';
                            return (
                                <div key={lId} style={{ background: '#1E293B', borderRadius: '16px', padding: '8px 6px', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <span style={{ fontSize: '10px', fontWeight: '900', color: '#94A3B8', marginBottom: '6px' }}>LOOM {loomIdLabel}</span>
                                    <div style={{ width: '100%', background: '#0F172A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '8px 4px', textAlign: 'center' }}>
                                        <span style={{ fontSize: '8px', fontWeight: '800', color: '#38BDF8', display: 'block', marginBottom: '2px' }}>{isNight ? 'NIGHT SHIFT' : 'DAY SHIFT'}</span>
                                        <span style={{ fontSize: '14px', fontWeight: '900', color: '#FFFFFF' }}>
                                            {parseFloat(meters).toFixed(1)} <span style={{ fontSize: '8px', color: '#94A3B8' }}>Meter</span>
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

              {/* Overtime / Badli Looms Grid & Duty Time Box */}
                {selectedDayForLooms.looms_ot && Object.values(selectedDayForLooms.looms_ot).some(m => parseFloat(m) > 0) && (() => {
                    const typeStr = selectedDayForLooms.otType || "";
                    const isOT = typeStr.includes('OT') && !typeStr.includes('BADLI');
                    const isBadli = typeStr.includes('BADLI') && !typeStr.includes('OT');
                    
                    let sectionTitle = "OVERTIME / BADLI PRODUCTION";
                    if (isOT) sectionTitle = "OVERTIME PRODUCTION";
                    if (isBadli) sectionTitle = "BADLI PRODUCTION";

                    let theme = { textMain: '#F59E0B', cardBorder: 'rgba(245, 158, 11, 0.3)', valText: '#FBBF24', titleLabel: 'BADLI', bg: 'rgba(245, 158, 11, 0.15)' };
                    if (isOT) {
                        theme = { textMain: '#38BDF8', cardBorder: 'rgba(56, 189, 248, 0.3)', valText: '#38BDF8', titleLabel: 'OVERTIME', bg: 'rgba(56, 189, 248, 0.15)' };
                    }

                    const otStart = selectedDayForLooms.otStartTime;
                    const otEnd = selectedDayForLooms.otEndTime;

                    return (
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <div style={{ height: '1px', flex: 1, background: theme.cardBorder }}></div>
                                <span style={{ fontSize: '10px', fontWeight: '800', color: theme.textMain, letterSpacing: '1px', textTransform: 'uppercase' }}>{sectionTitle}</span>
                                <div style={{ height: '1px', flex: 1, background: theme.cardBorder }}></div>
                            </div>

                            {/* 🌟 COMPACT OVERTIME / BADLI DUTY TIME BOX 🌟 */}
                            <div style={{ 
                                background: '#1E293B', 
                                padding: '10px 12px', 
                                borderRadius: '14px', 
                                display: 'flex', alignItems: 'center', gap: '10px', 
                                border: `1px solid ${theme.cardBorder}`,
                                boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                                marginBottom: '12px'
                            }}>
                                <div style={{ 
                                    background: theme.bg, 
                                    padding: '8px', borderRadius: '10px', 
                                    border: `1px solid ${theme.cardBorder}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <Clock size={18} color={theme.textMain} strokeWidth={2.5} />
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%', minWidth: 0 }}>
                                    {otStart && otEnd ? (
                                        (() => {
                                            const sMins = parseTimeStr(otStart);
                                            const eMins = parseTimeStr(otEnd);
                                            let diff = eMins - sMins;
                                            if (diff < 0) diff += 1440;
                                            const h = Math.floor(diff / 60);
                                            const m = diff % 60;
                                            const durStr = m > 0 ? `${h}:${String(m).padStart(2, '0')} hrs` : `${h} hrs`;

                                            return (
                                                <>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                                        <span style={{ fontSize: '9px', fontWeight: '800', color: theme.textMain, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                                                            {isBadli ? 'BADLI DUTY TIME' : 'OVERTIME DUTY TIME'}
                                                        </span>
                                                        <span style={{ 
                                                            fontSize: '9px', fontWeight: '900', color: theme.textMain, 
                                                            background: theme.bg, padding: '2px 7px', 
                                                            borderRadius: '6px', border: `1px solid ${theme.cardBorder}`,
                                                            letterSpacing: '0.3px', whiteSpace: 'nowrap'
                                                        }}>
                                                            {durStr}
                                                        </span>
                                                    </div>

                                                    <div style={{ fontSize: '13px', fontWeight: '900', color: '#FFFFFF', letterSpacing: '-0.2px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                        {formatTimeForDisplay(otStart)}
                                                        <span style={{ fontSize: '9px', color: theme.textMain, fontWeight: '800' }}>TO</span>
                                                        {formatTimeForDisplay(otEnd)}
                                                    </div>
                                                </>
                                            );
                                        })()
                                    ) : (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <span style={{ fontSize: '9px', fontWeight: '800', color: theme.textMain, textTransform: 'uppercase' }}>
                                                    {isBadli ? 'BADLI DUTY TIME' : 'OVERTIME DUTY TIME'}
                                                </span>
                                                <span style={{ fontSize: '12px', fontWeight: '900', color: '#FFFFFF' }}>
                                                    EXTRA SHIFT DUTY
                                                </span>
                                            </div>
                                            <span style={{ 
                                                fontSize: '9px', fontWeight: '900', color: theme.textMain, 
                                                background: theme.bg, padding: '2px 7px', 
                                                borderRadius: '6px', border: `1px solid ${theme.cardBorder}`
                                            }}>
                                                {isBadli ? 'BADLI' : 'OVERTIME'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                                {Object.entries(selectedDayForLooms.looms_ot).map(([lId, meters]) => {
                                    if(parseFloat(meters) <= 0) return null;
                                    return (
                                        <div key={`ot-${lId}`} style={{ background: '#1E293B', borderRadius: '16px', padding: '8px 6px', border: `1px solid ${theme.cardBorder}`, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <span style={{ fontSize: '10px', fontWeight: '900', color: theme.textMain, marginBottom: '6px' }}>LOOM {String(lId).padStart(2, '0')}</span>
                                            <div style={{ width: '100%', background: '#0F172A', border: `1px solid ${theme.cardBorder}`, borderRadius: '10px', padding: '8px 4px', textAlign: 'center' }}>
                                                <span style={{ fontSize: '8px', fontWeight: '800', color: theme.textMain, display: 'block', marginBottom: '2px' }}>{theme.titleLabel}</span>
                                                <span style={{ fontSize: '14px', fontWeight: '900', color: theme.valText }}>
                                                    {parseFloat(meters).toFixed(1)} <span style={{ fontSize: '8px', color: '#94A3B8' }}>Meter</span>
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })()}

                {/* Total Production & Earning Card (WITH REGULAR + OT/BADLI BREAKDOWN) */}
                {(() => {
                    const grandTotalProd = parseFloat(selectedDayForLooms.total || 0);
                    const otProd = parseFloat(selectedDayForLooms.otBadli || 0);
                    const regProd = Math.max(0, grandTotalProd - otProd);
                    const rate = parseFloat(myLiveApiData?.rate_per_meter || myLiveApiData?.ratePerMeter || 0);
                    const grandTotalEarn = grandTotalProd * rate;

                    const typeStr = selectedDayForLooms.otType || "";
                    const isOT = typeStr.includes('OT') && !typeStr.includes('BADLI');
                    const isBadli = typeStr.includes('BADLI') && !typeStr.includes('OT');
                    const isBoth = typeStr.includes('OT') && typeStr.includes('BADLI');

                    let otMeters = isBoth ? otProd / 2 : (isOT ? otProd : 0);
                    let badliMeters = isBoth ? otProd / 2 : (isBadli ? otProd : 0);
                    if (!isOT && !isBadli && !isBoth) {
                        otMeters = otProd;
                    }

                    return (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1E293B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '12px 16px', marginTop: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ background: 'rgba(56, 189, 248, 0.15)', padding: '10px', borderRadius: '12px' }}>
                                    <Layers size={20} color="#38BDF8" />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <span style={{ fontSize: '9px', fontWeight: '800', color: '#94A3B8', letterSpacing: '0.5px' }}>TOTAL PRODUCTION</span>
                                    
                                    {regProd > 0 && otProd > 0 ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                            {/* Regular Duty Production */}
                                            <span style={{ fontSize: '13px', fontWeight: '900', color: '#FFFFFF' }}>
                                                {regProd.toFixed(1)}
                                            </span>
                                            <span style={{ fontSize: '11px', color: '#64748B', fontWeight: '900' }}>+</span>
                                            
                                            {/* Overtime (Blue) */}
                                            {otMeters > 0 && (
                                                <span style={{ fontSize: '13px', fontWeight: '900', color: '#38BDF8' }}>
                                                    {otMeters.toFixed(1)} <span style={{ fontSize: '9px', fontWeight: '800' }}>OT</span>
                                                </span>
                                            )}

                                            {/* Badli (Yellow) */}
                                            {badliMeters > 0 && (
                                                <span style={{ fontSize: '13px', fontWeight: '900', color: '#FBBF24' }}>
                                                    {badliMeters.toFixed(1)} <span style={{ fontSize: '9px', fontWeight: '800' }}>BADLI</span>
                                                </span>
                                            )}

                                            <span style={{ fontSize: '11px', color: '#64748B', fontWeight: '900' }}>=</span>
                                            
                                            {/* Grand Total */}
                                            <span style={{ fontSize: '15px', fontWeight: '900', color: '#FFFFFF' }}>
                                                {grandTotalProd.toFixed(1)} <span style={{ fontSize: '9px', color: '#38BDF8' }}>Meter</span>
                                            </span>
                                        </div>
                                    ) : (
                                        <span style={{ fontSize: '16px', fontWeight: '900', color: '#FFFFFF' }}>
                                            {grandTotalProd.toFixed(1)} <span style={{ fontSize: '10px', color: '#38BDF8' }}>METER</span>
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', textAlign: 'right' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-end' }}>
                                    <span style={{ fontSize: '9px', fontWeight: '800', color: '#34D399', letterSpacing: '0.5px' }}>TOTAL EARNING</span>
                                    <span style={{ fontSize: '16px', fontWeight: '900', color: '#34D399' }}>
                                        ₹{grandTotalEarn.toFixed(0)}
                                    </span>
                                </div>
                                <div style={{ background: 'rgba(52, 211, 153, 0.15)', padding: '10px', borderRadius: '12px' }}>
                                    <IndianRupee size={20} color="#34D399" />
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>
        ) : (
            /* 🌟 MAIN TIMELINE TABLE VIEW 🌟 */
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, background: '#0F172A' }}>
                
                {/* TOP HEADER BANNER */}
                <div style={{ 
                    padding: '14px 18px', 
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)', 
                    background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                    flexShrink: 0, position: 'relative', zIndex: 10, 
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    width: '100%', boxSizing: 'border-box', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                }}>
                   <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', zIndex: 2 }}>
                        {activeHeaderInfo.mainTitle && (
                            <h2 style={{ fontSize: '16px', fontWeight: '900', color: '#FFFFFF', margin: 0, letterSpacing: '-0.3px', textTransform: 'uppercase' }}>
                                {activeHeaderInfo.mainTitle}
                            </h2>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>                            
                            {/* 🔥 NAYA BACK BUTTON: Sirf Monthly/Weekly view (isBackVisible) par dikhega 🔥 */}
                            {isBackVisible && (
                                <button onClick={(e) => {
                                    handleVoiceClick ? handleVoiceClick(e, "बैक जा रहे हैं।", handleBack) : handleBack();
                                }} style={{ 
                                    background: '#1E293B', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.15)', 
                                    cursor: 'pointer', padding: '3px 8px', display: 'flex', alignItems: 'center', 
                                    justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', transition: 'all 0.2s ease' 
                                }}>
                                    <ArrowLeft size={13} color="#FFFFFF" strokeWidth={2.5}/>
                                </button>
                            )}

                         {/* Date Strip */}
                            <span style={{ 
                                fontSize: '10px', color: '#38BDF8', fontWeight: '800', 
                                background: 'rgba(255, 255, 255, 0.08)', padding: '4px 10px', borderRadius: '8px', 
                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                display: 'inline-flex', alignItems: 'center', gap: '5px' 
                            }}>
                                <CalendarDays size={11} color="#38BDF8" strokeWidth={2.5} /> 
                                {activeHeaderInfo.dateStripText}
                            </span>
                            
                            {/* 🔥 DAILY / WEEKLY TOGGLE SWITCH 🔥 */}
                            {selectedMonthForDays && !selectedWeekForDays && (
                                <div style={{ 
                                    display: 'flex', alignItems: 'center', background: 'rgba(255, 255, 255, 0.05)', 
                                    borderRadius: '8px', padding: '2px', border: '1px solid rgba(255,255,255,0.1)', marginLeft: '4px'
                                }}>
                                    <button 
                                        onClick={() => setMonthViewMode('daily')}
                                        style={{ 
                                            background: monthViewMode === 'daily' ? '#38BDF8' : 'transparent', 
                                            color: monthViewMode === 'daily' ? '#0F172A' : '#94A3B8', 
                                            border: 'none', borderRadius: '6px', padding: '3px 8px', 
                                            fontSize: '9px', fontWeight: '900', cursor: 'pointer', transition: 'all 0.2s' 
                                        }}>
                                        DAILY
                                    </button>
                                    <button 
                                        onClick={() => setMonthViewMode('weekly')}
                                        style={{ 
                                            background: monthViewMode === 'weekly' ? '#38BDF8' : 'transparent', 
                                            color: monthViewMode === 'weekly' ? '#0F172A' : '#94A3B8', 
                                            border: 'none', borderRadius: '6px', padding: '3px 8px', 
                                            fontSize: '9px', fontWeight: '900', cursor: 'pointer', transition: 'all 0.2s' 
                                        }}>
                                        WEEKLY
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* HIDE / SHOW EARNINGS TOGGLE BUTTON */}
                    <div style={{ 
                        display: 'flex', alignItems: 'center', gap: '10px', 
                        background: 'rgba(255, 255, 255, 0.08)', padding: '6px 12px', borderRadius: '30px', 
                        border: '1px solid rgba(255, 255, 255, 0.15)', marginLeft: 'auto' 
                    }}>
                        <span style={{ fontSize: '10px', fontWeight: '900', color: showEarnings ? '#34D399' : '#94A3B8', letterSpacing: '0.5px' }}>
                            {showEarnings ? 'HIDE EARNINGS' : 'SHOW EARNINGS'}
                        </span>
                        <button
                            onClick={() => setShowEarnings(!showEarnings)}
                            style={{
                                position: 'relative', width: '40px', height: '22px', 
                                background: showEarnings ? '#10B981' : '#334155', 
                                borderRadius: '20px', border: 'none', cursor: 'pointer', 
                                transition: 'all 0.3s ease', display: 'flex', alignItems: 'center', padding: '2px'
                            }}
                        >
                            <div style={{
                                width: '18px', height: '18px', background: '#FFFFFF', borderRadius: '50%', 
                                transition: 'all 0.3s ease', transform: showEarnings ? 'translateX(18px)' : 'translateX(0)', 
                                display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                            }}>
                                <IndianRupee size={10} color={showEarnings ? '#10B981' : '#64748B'} strokeWidth={3.5} />
                            </div>
                        </button>
                    </div>
                </div>

                {/* SCROLLABLE TABLE AREA */}
                {(() => {
                    const hasDayData = filteredReportData.some(r => parseFloat(r.day || 0) > 0);
                    const hasNightData = filteredReportData.some(r => parseFloat(r.night || 0) > 0);
                    const hasOTData = filteredReportData.some(r => parseFloat(r.otBadli || 0) > 0 && (!r.otType || r.otType.includes('OT')));
                    const hasBadliData = filteredReportData.some(r => parseFloat(r.otBadli || 0) > 0 && r.otType && r.otType.includes('BADLI'));

                    return (
                        <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 12px 8px', background: '#0B1329' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10.5px', tableLayout: 'auto' }}>
                                <thead style={{ position: 'sticky', top: 0, background: '#0F172A', zIndex: 5, boxShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
                                    <tr style={{ textAlign: 'left', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        <th style={{ padding: '12px 6px', borderBottom: '1px solid rgba(255,255,255,0.1)', fontSize: '10px', fontWeight: '900', color: '#94A3B8' }}>TIMELINE</th>
                                        <th style={{ padding: '12px 4px', borderBottom: '1px solid rgba(255,255,255,0.1)', fontSize: '9px', fontWeight: '900', background: 'rgba(255,255,255,0.02)', textAlign: 'center', color: '#94A3B8', opacity: hasDayData ? 1 : 0.4, width: !hasDayData ? '1%' : undefined, whiteSpace: !hasDayData ? 'nowrap' : undefined }}>DAY</th>
                                        <th style={{ padding: '12px 4px', borderBottom: '1px solid rgba(255,255,255,0.1)', fontSize: '9px', fontWeight: '900', background: 'rgba(255,255,255,0.04)', textAlign: 'center', color: '#94A3B8', opacity: hasNightData ? 1 : 0.4, width: !hasNightData ? '1%' : undefined, whiteSpace: !hasNightData ? 'nowrap' : undefined }}>NIGHT</th>
                                        <th style={{ padding: '12px 4px', borderBottom: '1px solid rgba(255,255,255,0.1)', fontSize: '9px', fontWeight: '900', background: 'rgba(56, 189, 248, 0.08)', textAlign: 'center', color: '#38BDF8', opacity: hasOTData ? 1 : 0.4, width: !hasOTData ? '1%' : undefined, whiteSpace: !hasOTData ? 'nowrap' : undefined }}>OVERTIME</th>
                                        <th style={{ padding: '12px 4px', borderBottom: '1px solid rgba(255,255,255,0.1)', fontSize: '9px', fontWeight: '900', background: 'rgba(245, 158, 11, 0.08)', textAlign: 'center', color: '#F59E0B', opacity: hasBadliData ? 1 : 0.4, width: !hasBadliData ? '1%' : undefined, whiteSpace: !hasBadliData ? 'nowrap' : undefined }}>BADLI</th>
                                        <th style={{ padding: '12px 6px', textAlign: 'right', borderBottom: '1px solid rgba(255,255,255,0.1)', fontSize: '9px', fontWeight: '900', color: '#FFFFFF' }}>TOTAL</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredReportData.length > 0 ? filteredReportData.map((row, idx) => {
                                        let otVal = 0;
                                        let badliVal = 0;
                                        const totalOTBadli = parseFloat(row.otBadli || 0);
                                        
                                        if (totalOTBadli > 0) {
                                            const typeStr = row.otType || "";
                                            if (typeStr.includes('OT') && typeStr.includes('BADLI')) {
                                                otVal = totalOTBadli / 2;
                                                badliVal = totalOTBadli / 2;
                                            } else if (typeStr.includes('BADLI')) {
                                                badliVal = totalOTBadli;
                                            } else {
                                                otVal = totalOTBadli;
                                            }
                                        }

                                        const hasProduction = (parseFloat(row.total || 0) > 0) || (totalOTBadli > 0);
                                        let rowVoiceMsg = null;
                                        if (row.isYearRow) {
                                            if (hasProduction) rowVoiceMsg = `साल ${row.yearVal} में वर्कर का टोटल ${parseFloat(row.total).toFixed(1)} मीटर प्रोडक्शन हुआ है।`;
                                        } else if (row.isMonthRow) {
                                            if (hasProduction) rowVoiceMsg = `${row.monthVal} महीने में वर्कर का टोटल ${parseFloat(row.total).toFixed(1)} मीटर प्रोडक्शन हुआ है।`;
                                        } else {
                                            const dayNameMap = { 'Sunday': ' इतवार', 'Monday': ' पीर', 'Tuesday': ' मंगल', 'Wednesday': ' बुध', 'Thursday': ' जुमेरात', 'Friday': ' जुम्मा', 'Saturday': ' शनिवार' };
                                            const hindiDay = dayNameMap[row.subLabel] || row.subLabel;
                                            if (hasProduction) rowVoiceMsg = `${row.label} को ${hindiDay} के दिन वर्कर का टोटल ${parseFloat(row.total).toFixed(1)} मीटर प्रोडक्शन हुआ है।`;
                                        }
                                        
                                        return (
                                            <tr key={idx} ref={row.isCurrent ? currentRowRef : null} 
                                                onClick={(e) => { if (rowVoiceMsg && handleVoiceClick) { window.speechSynthesis.cancel(); handleVoiceClick(e, rowVoiceMsg, null); } }}
                                                style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', borderLeft: row.isCurrent ? '3px solid #10B981' : '3px solid transparent', background: 'transparent', cursor: rowVoiceMsg ? 'pointer' : 'default' }}>
                                                
                                            {/* TIMELINE COLUMN */}
                                                <td style={{ padding: '10px 6px', paddingLeft: row.isCurrent ? '10px' : '6px', width: '1%', whiteSpace: 'nowrap' }}>
                                                    {row.isWeekRow ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap' }}>
                                                                
                                                                {/* WEEK LABEL & BADGE STACKED */}
                                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                                                                    <span style={{ fontWeight: '900', color: row.isCurrent ? '#34D399' : '#FFFFFF', fontSize: '13px', whiteSpace: 'nowrap' }}>
                                                                        {row.label}
                                                                    </span>
                                                                    {row.isCurrent && (
                                                                        <span className="forced-white-badge" style={{ fontSize: '7px', background: '#10B981', padding: '1px 4px', borderRadius: '4px', whiteSpace: 'nowrap', width: 'fit-content', lineHeight: 1 }}>
                                                                            CURRENT
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                <button 
                                                                    onClick={(e) => { e.stopPropagation(); if(window.speechSynthesis) window.speechSynthesis.cancel(); setSelectedWeekForDays(row.weekVal); }} 
                                                                    style={{ 
                                                                        fontSize: '8.5px', color: '#38BDF8', cursor: 'pointer', fontWeight: '900', 
                                                                        display: 'inline-flex', alignItems: 'center', gap: '2px', 
                                                                        background: 'rgba(56, 189, 248, 0.15)', border: '1px solid rgba(56, 189, 248, 0.35)', 
                                                                        padding: '2px 6px', borderRadius: '6px', whiteSpace: 'nowrap', flexShrink: 0 
                                                                    }}
                                                                >
                                                                    VIEW <ChevronRight size={10} color="#38BDF8" strokeWidth={3} />
                                                                </button>
                                                            </div>

                                                            <span style={{ fontSize: '8.5px', color: '#94A3B8', fontWeight: '700', background: 'rgba(255,255,255,0.04)', padding: '2px 5px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.06)', display: 'inline-flex', alignItems: 'center', gap: '3px', width: 'fit-content', whiteSpace: 'nowrap' }}>
                                                                <CalendarDays size={9} color="#94A3B8" />
                                                                {row.subLabel.replace('-', ' TO ')}
                                                            </span>
                                                        </div>
                                                  ) : row.isYearRow ? (
                                                        /* 🌟 YEAR ROW LOGIC (Badge inline, Button below) 🌟 */
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <div style={{ fontWeight: '900', color: row.isCurrent ? '#34D399' : '#FFFFFF', fontSize: '13px', whiteSpace: 'nowrap' }}>
                                                                    {row.label}
                                                                </div>
                                                                {row.isCurrent && (
                                                                    <span className="forced-white-badge" style={{ fontSize: '7px', background: '#10B981', padding: '1px 4px', borderRadius: '4px', width: 'fit-content', lineHeight: 1 }}>
                                                                        CURRENT
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div onClick={(e) => { e.stopPropagation(); if(window.speechSynthesis) window.speechSynthesis.cancel(); setSelectedYearForMonths(row.yearVal); }} style={{ fontSize: '9px', color: '#38BDF8', cursor: 'pointer', fontWeight: '800', display: 'inline-flex', alignItems: 'center', gap: '3px', background: 'rgba(30, 41, 59, 0.7)', border: '1px solid rgba(255,255,255,0.12)', padding: '3px 7px', borderRadius: '6px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                                View Monthly <ArrowRightLeft size={10} />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                       /* 🌟 MONTH/DAY ROW LOGIC (Badge below, Button inline) 🌟 */
                                                       <>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
                                                                
                                                                {/* MONTH/DAY LABEL & BADGE STACKED */}
                                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                                                                    <div style={{ fontWeight: '900', color: row.isCurrent ? '#34D399' : '#FFFFFF', fontSize: '13px', whiteSpace: 'nowrap' }}>
                                                                        {row.label}
                                                                    </div>
                                                                    {row.isCurrent && <span className="forced-white-badge" style={{ fontSize: '7px', background: '#10B981', padding: '1px 4px', borderRadius: '4px', width: 'fit-content', lineHeight: 1 }}>CURRENT</span>}
                                                                </div>
                                                                
                                                                {row.isMonthRow && (
                                                                    <div onClick={(e) => { e.stopPropagation(); if(window.speechSynthesis) window.speechSynthesis.cancel(); setSelectedMonthForDays(row.monthVal); setMonthViewMode('daily'); }} style={{ fontSize: '9px', color: '#38BDF8', cursor: 'pointer', fontWeight: '800', display: 'inline-flex', alignItems: 'center', gap: '3px', background: 'rgba(30, 41, 59, 0.7)', border: '1px solid rgba(255,255,255,0.12)', padding: '3px 7px', borderRadius: '6px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                                        VIEW <ArrowRightLeft size={10} />
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* WEEKDAY SUBLABEL FOR REGULAR DAYS */}
                                                            {!row.isMonthRow && (
                                                                <div style={{ fontSize: '10px', color: '#94A3B8', fontWeight: '600', marginTop: '2px', whiteSpace: 'nowrap' }}>{row.subLabel}</div>
                                                            )}
                                                        </>
                                                    )}
                                                </td>
                                                
                                                {hasProduction ? (
                                                    <>
                                                        {/* DAY COLUMN */}
                                                        <td style={{ padding: '10px 4px', background: 'rgba(255,255,255,0.02)', textAlign: 'center' }}>
                                                            {parseFloat(row.day || 0) > 0 ? (
                                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                                    <span style={{ fontSize: '12px', color: '#FFFFFF', fontWeight: '800' }}>{parseFloat(row.day).toFixed(1)}</span>
                                                                    <span style={{ fontSize: '7.5px', color: '#94A3B8' }}>Meter</span>
                                                                    {showEarnings && (
                                                                        <span style={{ marginTop: '2px', background: 'rgba(16, 185, 129, 0.2)', color: '#34D399', border: '1px solid rgba(16, 185, 129, 0.4)', padding: '1px 5px', borderRadius: '4px', fontSize: '9px', fontWeight: '900' }}>
                                                                            ₹{Math.round(row.dayEarn || 0)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span style={{ color: '#475569', fontSize: '12px', fontWeight: '900' }}>-</span>
                                                            )}
                                                        </td>

                                                        {/* NIGHT COLUMN */}
                                                        <td style={{ padding: '10px 4px', background: 'rgba(255,255,255,0.04)', textAlign: 'center' }}>
                                                            {parseFloat(row.night || 0) > 0 ? (
                                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                                    <span style={{ fontSize: '12px', color: '#FFFFFF', fontWeight: '800' }}>{parseFloat(row.night).toFixed(1)}</span>
                                                                    <span style={{ fontSize: '7.5px', color: '#94A3B8' }}>Meter</span>
                                                                    {showEarnings && (
                                                                        <span style={{ marginTop: '2px', background: 'rgba(16, 185, 129, 0.2)', color: '#34D399', border: '1px solid rgba(16, 185, 129, 0.4)', padding: '1px 5px', borderRadius: '4px', fontSize: '9px', fontWeight: '900' }}>
                                                                            ₹{Math.round(row.nightEarn || 0)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span style={{ color: '#475569', fontSize: '12px', fontWeight: '900' }}>-</span>
                                                            )}
                                                        </td>

                                                        {/* OVERTIME COLUMN */}
                                                        <td style={{ padding: '10px 4px', background: 'rgba(56, 189, 248, 0.05)', textAlign: 'center' }}>
                                                            {otVal > 0 ? (
                                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                                    <span style={{ fontSize: '12px', color: '#38BDF8', fontWeight: '800' }}>{otVal.toFixed(1)}</span>
                                                                    <span style={{ fontSize: '7.5px', color: '#38BDF8', opacity: 0.8 }}>Meter</span>
                                                                    {showEarnings && (
                                                                        <span style={{ marginTop: '2px', background: 'rgba(56, 189, 248, 0.2)', color: '#38BDF8', border: '1px solid rgba(56, 189, 248, 0.4)', padding: '1px 5px', borderRadius: '4px', fontSize: '9px', fontWeight: '900' }}>
                                                                            ₹{Math.round((row.otBadliEarn || 0) * (otVal / (otVal + badliVal)))}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span style={{ color: '#1E293B', fontSize: '12px', fontWeight: '900' }}>-</span>
                                                            )}
                                                        </td>

                                                        {/* BADLI COLUMN */}
                                                        <td style={{ padding: '10px 4px', background: 'rgba(245, 158, 11, 0.05)', textAlign: 'center' }}>
                                                            {badliVal > 0 ? (
                                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                                    <span style={{ fontSize: '12px', color: '#FBBF24', fontWeight: '800' }}>{badliVal.toFixed(1)}</span>
                                                                    <span style={{ fontSize: '7.5px', color: '#FBBF24', opacity: 0.8 }}>Meter</span>
                                                                    {showEarnings && (
                                                                        <span style={{ marginTop: '2px', background: 'rgba(245, 158, 11, 0.2)', color: '#FBBF24', border: '1px solid rgba(245, 158, 11, 0.4)', padding: '1px 5px', borderRadius: '4px', fontSize: '9px', fontWeight: '900' }}>
                                                                            ₹{Math.round((row.otBadliEarn || 0) * (badliVal / (otVal + badliVal)))}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span style={{ color: '#1E293B', fontSize: '12px', fontWeight: '900' }}>-</span>
                                                            )}
                                                        </td>

                                                        {/* TOTAL COLUMN */}
                                                        <td style={{ padding: '10px 6px', textAlign: 'right' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
                                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                                    <span style={{ fontSize: '13px', color: '#FFFFFF', fontWeight: '900' }}>{parseFloat(row.total || 0).toFixed(1)}</span>
                                                                    <span style={{ fontSize: '8px', color: '#94A3B8', fontWeight: '700' }}>Meter</span>
                                                                    {showEarnings && (
                                                                        <span style={{ marginTop: '2px', background: 'rgba(16, 185, 129, 0.25)', color: '#34D399', border: '1px solid #10B981', padding: '1px 5px', borderRadius: '4px', fontSize: '10px', fontWeight: '900' }}>
                                                                            ₹{Math.round(row.totalEarn || 0)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                
                                                                {!row.isYearRow && !row.isMonthRow && !row.isWeekRow && (
                                                                    <div 
                                                                        onClick={(e) => { 
                                                                            e.stopPropagation(); 
                                                                            if(window.speechSynthesis) window.speechSynthesis.cancel(); 
                                                                            setSelectedDayForLooms(row); 
                                                                        }} 
                                                                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(255,255,255,0.1)', padding: '5px', borderRadius: '6px' }}
                                                                    >
                                                                        <ChevronRight size={13} color="#94A3B8" strokeWidth={2.5} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <td colSpan="5" style={{ padding: '10px 4px', textAlign: 'center', verticalAlign: 'middle', background: 'rgba(255,255,255,0.01)' }}>
                                                        <span style={{ fontSize: '8.5px', fontWeight: '800', color: '#475569', letterSpacing: '0.5px' }}>NO PRODUCTION</span>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    }) : (
                                        <tr><td colSpan="6" style={{ padding: '30px', textAlign: 'center', color: '#64748B', fontWeight: '600' }}>No Records Found</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    );
                })()}
            </div>
        )}
    </div>
</div>

            {/* 🌟 EDIT WORKER MODAL INTEGRATION FOR OWNER 🌟 */}
            <EditWorkerModal 
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                workerData={worker}
                onSave={async (updatedWorkerData) => {
                    if (refreshData) refreshData();
                    if (updatedWorkerData) setWorker(updatedWorkerData);
                    setIsEditModalOpen(false);
                }}
                availableLooms={availableLooms}
                handleVoiceClick={handleVoiceClick}
                handleFocusVoice={handleFocusVoice}
            />

          
            {/* 🌟 DARK BLUE PREMIUM PDF EXPORT MODAL 🌟 */}
            <Modal 
                isOpen={showReportModal} 
                onClose={() => setShowReportModal(false)} 
                title="DOWNLOAD REPORT" 
                subtitle="PDF EXPORT SETTINGS"
                icon={Download} 
                iconBg="rgba(56, 189, 248, 0.15)" 
                iconBorder="rgba(56, 189, 248, 0.3)"
                iconColor="#38BDF8" 
                maxWidth="380px"
                bg="#0F172A"
                headerBg="#1E293B"
                borderColor="rgba(255, 255, 255, 0.15)"
                titleColor="#FFFFFF"
                subtitleColor="#38BDF8"
                top="0px"
                bottom="0px"
                zIndex={999999}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                    
                    {/* 1. Report Frequency Selection */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
                        <label style={{ fontSize: '9px', fontWeight: '900', color: '#38BDF8', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                            REPORT FREQUENCY
                        </label>
                        <div style={{ position: 'relative', width: '100%' }}>
                            <select 
                                value={reportFrequency} 
                                onChange={e => setReportFrequency(e.target.value)} 
                                style={{
                                    width: '100%',
                                    background: '#1E293B',
                                    color: '#FFFFFF',
                                    border: '1px solid rgba(255, 255, 255, 0.12)',
                                    borderRadius: '14px',
                                    padding: '12px 14px',
                                    fontSize: '12px',
                                    fontWeight: '800',
                                    outline: 'none',
                                    appearance: 'none',
                                    WebkitAppearance: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                <option value="daily" style={{ background: '#1E293B', color: '#FFF' }}>DAILY REPORT</option>
                                <option value="weekly" style={{ background: '#1E293B', color: '#FFF' }}>WEEKLY REPORT</option>
                                <option value="monthly" style={{ background: '#1E293B', color: '#FFF' }}>MONTHLY REPORT</option>
                            </select>
                            <ChevronDown size={14} color="#94A3B8" style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                        </div>
                    </div>

                    {/* 2. Report Type Selection (Detailed / Summary) - ADDED BOX */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
                        <label style={{ fontSize: '9px', fontWeight: '900', color: '#38BDF8', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                            REPORT TYPE
                        </label>
                        <div style={{ position: 'relative', width: '100%' }}>
                            <select 
                                value={reportType} 
                                onChange={e => setReportType(e.target.value)} 
                                style={{
                                    width: '100%',
                                    background: '#1E293B',
                                    color: '#FFFFFF',
                                    border: '1px solid rgba(255, 255, 255, 0.12)',
                                    borderRadius: '14px',
                                    padding: '12px 14px',
                                    fontSize: '12px',
                                    fontWeight: '800',
                                    outline: 'none',
                                    appearance: 'none',
                                    WebkitAppearance: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                <option value="detailed" style={{ background: '#1E293B', color: '#FFF' }}>FULL DETAILED REPORT</option>
                                <option value="summary" style={{ background: '#1E293B', color: '#FFF' }}>SUMMARY ONLY (FAST)</option>
                            </select>
                            <ChevronDown size={14} color="#94A3B8" style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                        </div>
                    </div>

                    {/* 3. Date Range Selection Box */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
                        <label style={{ fontSize: '9px', fontWeight: '900', color: '#38BDF8', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                            SELECT DATE RANGE
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '8px', alignItems: 'center', background: '#1E293B', padding: '10px 12px', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.1)', width: '100%', boxSizing: 'border-box' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <span style={{ fontSize: '8px', fontWeight: '900', color: '#38BDF8', textTransform: 'uppercase' }}>FROM</span>
                                <input 
                                    type="date" 
                                    value={reportDateStart} 
                                    onChange={(e) => setReportDateStart(e.target.value)} 
                                    style={{
                                        width: '100%', background: 'transparent', border: 'none',
                                        color: '#FFFFFF', fontSize: '11px', fontWeight: '800', outline: 'none',
                                        colorScheme: 'dark'
                                    }} 
                                />
                            </div>

                            <div style={{ background: '#0F172A', padding: '5px 8px', borderRadius: '8px', fontSize: '10px', color: '#38BDF8', fontWeight: '900', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                ➔
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'right' }}>
                                <span style={{ fontSize: '8px', fontWeight: '900', color: '#F43F5E', textTransform: 'uppercase' }}>TO</span>
                                <input 
                                    type="date" 
                                    value={reportDateEnd} 
                                    onChange={(e) => setReportDateEnd(e.target.value)} 
                                    style={{
                                        width: '100%', background: 'transparent', border: 'none',
                                        color: '#FFFFFF', fontSize: '11px', fontWeight: '800', outline: 'none',
                                        textAlign: 'right', colorScheme: 'dark'
                                    }} 
                                />
                            </div>
                        </div>
                    </div>

                    {/* 4. Download Action Button */}
                    <button 
                        onClick={handleGenerateReport} 
                        disabled={isDownloading} 
                        style={{
                            width: '100%',
                            background: 'linear-gradient(135deg, #0284C7 0%, #38BDF8 100%)',
                            color: '#FFFFFF',
                            border: 'none',
                            borderRadius: '16px',
                            padding: '14px',
                            fontSize: '12px',
                            fontWeight: '900',
                            letterSpacing: '0.8px',
                            cursor: isDownloading ? 'not-allowed' : 'pointer',
                            opacity: isDownloading ? 0.7 : 1,
                            boxShadow: '0 8px 20px rgba(2, 132, 199, 0.4)',
                            transition: 'all 0.2s ease',
                            marginTop: '4px',
                            textTransform: 'uppercase'
                        }}
                    >
                        {isDownloading ? 'PROCESSING DETAILS...' : 'DOWNLOAD PRODUCTION REPORT'}
                    </button>

                </div>
            </Modal>
            

            {/* 🌟 SMART GUIDE MODAL 🌟 */}
            <Modal isOpen={showGuideModal} onClose={() => setShowGuideModal(false)} title="Smart Live Guide" icon={HelpCircle} iconColor="#0284C7" iconBg="#E0F2FE" maxWidth="400px">
                <div style={{ marginTop: '10px' }}>
                    <p style={{ fontSize: '9.5px', fontWeight: '800', color: '#64748B', marginBottom: '16px', textTransform: 'uppercase' }}>Aap kis topic ke bare me sikhna chahte hain?</p>
                    <div onClick={(e) => { setShowGuideModal(false); startSmartGuide('worker_report'); }} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', alignItems: 'center', background: '#f8fafc', borderRadius: '16px', cursor: 'pointer', border: '1px solid #e2e8f0' }}>
                        <span style={{ fontSize: '11px', fontWeight: '900', color: '#0F172A' }}>1. PDF Report kaise download karein?</span>
                        <PlayCircle size={14} color="#4338CA"/>
                    </div>
                </div>
            </Modal>

            {/* 🌟 LIVE GUIDE ACTIVE FLOATING PANEL 🌟 */}
            {activeGuideFlow && (
                <div style={{ position: 'fixed', bottom: '90px', right: '16px', zIndex: 100010, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                    <div style={{ background: 'rgba(255,255,255,0.95)', padding: '10px 14px', borderRadius: '12px', boxShadow: '0 8px 20px rgba(0,0,0,0.4)', border: '2px solid #10B981', width: '160px', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', fontWeight: '800', color: '#111' }}>Live Guide</div>
                        <div style={{ fontSize: '8px', color: '#444', fontWeight: '700', marginTop: '4px' }}>{currentGuideMessage || "Chamakte option ko chunein"}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={advanceNextStep} style={{ background: '#2563EB', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '8px', fontWeight: '800', fontSize: '8px' }}>Next Step ⏭</button>
                        <button onClick={() => endSmartGuide(true)} style={{ background: '#DC2626', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '8px', fontWeight: '800', fontSize: '8px' }}>Stop Guide</button>
                    </div>
                </div>
            )}
        </div>
    );
}